// src/components/IndividualRoles.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlayCircleIcon,
  CheckCircleIcon,
  TrophyIcon,
  ClipboardDocumentListIcon,
  LockClosedIcon,
  BookOpenIcon,
  XMarkIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  VideoCameraIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  SpeakerWaveIcon,
  ClockIcon,
  CheckCircleIcon as CheckCircleIconSolid,
  ArrowRightIcon,
  ChatBubbleBottomCenterTextIcon
} from "@heroicons/react/24/outline";
import { FaGithub as GitHubIcon } from "react-icons/fa";
import SubscriptionStatus from './SubscriptionStatus';
import VoiceAssistant from './NewVoiceAssistant';
import { useVoiceAssistant } from '../hooks/useVoiceAssistant';
import ThemeToggle from './ThemeToggle';
import { useTaskCompletion } from '../hooks/useTaskCompletion';
import SimplePracticeCodingButton from './SimplePracticeCodingButton';
import API_BASE_URL from '../Config/config';
import AchievementSystem from "./AchievementSystem";
import AIMentorController from "./AIMentorController";
import { checkLLMTrialStatus } from '../utils/llmTrialManager';
import { hasFullAccess, redirectToPaymentIfNeeded } from '../utils/paymentStatus';
import VisualizationPreview from"./shared/VisualizationPreview";

/* -------------------------------------------------------------------------- */
/* utils */
/* -------------------------------------------------------------------------- */
const origin = typeof window !== "undefined" ? window.location.origin : "";
const publicBase = `${origin.replace(/\/+$/, "")}/`;
const pub = (p) => {
  const path = String(p).startsWith("/") ? String(p).slice(1) : String(p);
  return `${publicBase}${path}`;
};
const PUBLIC_ROOT = "individualroles";
const isDark = false; 

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const slugify = (s = "") => {
  const str = s.toString().trim();
  // Legacy mapping for old enrollments missing skill_slug.
  // New enrollments will have skill_slug stored in DB and used directly.
  const mapping = {
    "Industry Career Accelerator": "industry-career-accelerator",
    "React.js + Node.js + PostgreSQL + RAG Basics": "react-node-postgresql-rag-basics",
    "Python + Django + REST API + PostgreSQL": "python-django-rest-api-postgresql",
    "React.js + SAP APIs + Node.js + PostgreSQL": "react-sap-apis-node-postgresql",
    "SAP Fiori + Java Spring Boot + Microservices + MySQL": "sap-fiori-javaspring-boot-microservices-mysql",
    "Flutter + Riverpod + Node.js + Express.js + PostgreSQL": "flutter-riverpod-node-express-postgresql",
    "TensorFlow + Keras + PyTorch + Scikit-learn + FastAPI + Hugging Face Transformers": "tensorflow-keras-pytorch-scikit-learn-fastapi-hugging-face-transformers",
    "Node.js + Express.js + PostgreSQL + Redis + WebSockets": "node-express-redis-postgresql-websockets",
    "FastAPI + MongoDB + Vector Databases + LangChain": "fastapi-mongodb-vector-databases-langchain",
    "AWS + Docker + Kubernetes + Terraform": "aws-docker-kubernetes-terraform",
    "Azure CICD + Helm + Monitoring + Grafana + Prometheus": "azure-cicd-helm-monitoring-grafana-prometheus",
    "HTML + CSS + JavaScript + Bootstrap + Tailwind": "html-css-javascript-bootstrap-tailwind",
    "Java Spring Boot + Hibernate + Microservices": "java-spring-boot-hibernate-microservices",
    "JavaScript + React + Node + Express": "javascript-react-node-express",
    "Linux Shell Scripting + Git + Jenkins": "linux-shell-scripting-git-jenkins",
    "Python + NumPy + Pandas + Matplotlib + Seaborn": "python-numpy-pandas-matplotlib-seaborn",
    "SQL + PL/SQL + Data Warehousing + ETL Tools": "sql-pl-sql-data-warehousing-etl-tools",
    "TensorFlow + Keras + PyTorch + Scikit-learn": "tensorflow-keras-pytorch-scikit-learn"
  };

  if (mapping[str]) return mapping[str];

  return str.toLowerCase()
    .replace(/&/g, " and")
    .replace(/\+/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
};

// Safely convert explanation values to strings (handles objects, arrays, etc.)
const safeToString = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(safeToString).join('. ');
  if (typeof value === 'object') {
    if (value.topicOverview) return safeToString(value.topicOverview);
    if (value.explanation) return safeToString(value.explanation);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

// Convert CloudFront/S3 URLs to proxy URLs to avoid CORS issues
const getProxiedVideoUrl = (url) => {
 if (!url) return url;
 
 // Check if URL is from CloudFront or S3
 const cloudfrontPattern = /https?:\/\/d1atm4dw3zgpab\.cloudfront\.net\//;
 const s3Pattern = /https?:\/\/.*\.s3\.(.*\.)?amazonaws\.com\//;
 
 if (cloudfrontPattern.test(url) || s3Pattern.test(url)) {
 // Convert to proxy URL
 return `${API_BASE_URL}/api/video/proxy?url=${encodeURIComponent(url)}`;
 }
 
 // Not a CloudFront/S3 URL, return as is
 return url;
};

/* -------------------------------------------------------------------------- */
/* local storage */
/* -------------------------------------------------------------------------- */
const pKey = (userId, tag) => `progress_${userId}_${slugify(tag)}`;
const readProgress = (userId, tag) => {
 try {
 const raw = localStorage.getItem(pKey(userId, tag));
 if (!raw) return { completedTaskIds: [], reviewStateById: {} };
 const parsed = JSON.parse(raw);
 if (Array.isArray(parsed)) return { completedTaskIds: parsed, reviewStateById: {} };
 return { completedTaskIds: parsed.completedTaskIds || [], reviewStateById: parsed.reviewStateById || {} };
 } catch { return { completedTaskIds: [], reviewStateById: {} }; }
};
const writeProgress = (userId, tag, obj) => {
  try {
    localStorage.setItem(pKey(userId, tag), JSON.stringify(obj));
    // Dispatch event to notify other components (like CertificatePage) that progress has changed
    window.dispatchEvent(new CustomEvent('skillgenProgressUpdated', { detail: { userId, tag } }));
  } catch {}
};

const mKey = (userId, tag) => `mentor_${userId}_${slugify(tag)}`;
const readChat = (userId, tag) => { try { return JSON.parse(localStorage.getItem(mKey(userId, tag)) ||"[]"); } catch { return []; } };
const writeChat = (userId, tag, msgs) => { try { localStorage.setItem(mKey(userId, tag), JSON.stringify(msgs)); } catch {} };

/* -------------------------------------------------------------------------- */
/* Progress Sync Function */
/* -------------------------------------------------------------------------- */
const syncProgressToBackend = async (userId, bunchName, completedTaskIds, totalTasks) => {
 try {
 const progressPercentage = totalTasks > 0 ? Math.round((completedTaskIds.length / totalTasks) * 100) : 0;
 
 console.log('🔄 Syncing progress to backend:', {
 userId,
 bunchName,
 completedTaskIds,
 progressPercentage,
 totalTasks,
 url: `${API_BASE_URL}/api/certificate/sync-progress`
 });
 
 const sessionToken = localStorage.getItem('session_token');
 // Avoid sending X-Session-Token for student tenant flow to prevent CORS preflight failures
 const currentUser = JSON.parse(localStorage.getItem('user') || "{}");
 const isMainPlatform = window.location.hostname === 'www.theskillgenie.com' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
 const isStudentTenant = currentUser && (currentUser.role === 'student' && (currentUser.tenant || currentUser.college_id || !isMainPlatform));

 const headers = { 'Content-Type': 'application/json' };
 if (!isStudentTenant) headers['user-id'] = userId || '';
 if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
 if (sessionToken && !isStudentTenant) headers['X-Session-Token'] = sessionToken;

 const response = await fetch(`${API_BASE_URL}/api/certificate/sync-progress`, {
 method: 'POST',
 headers,
 body: JSON.stringify({
 userId,
 bunchName,
 completedTaskIds,
 progressPercentage,
 totalTasks
 })
 });

 if (response.ok) {
 const result = await response.json();
 console.log('✅ Progress synced to backend successfully:', result);
 } else {
 const errorText = await response.text();
 console.error('❌ Failed to sync progress to backend:', response.status, errorText);
 }
 } catch (error) {
 console.error('❌ Error syncing progress to backend:', error);
 }
};

/* -------------------------------------------------------------------------- */
/* backend calls */
/* -------------------------------------------------------------------------- */
async function fetchUserContext(userId) {
 const sessionToken = localStorage.getItem('session_token');
 // Do not send X-Session-Token for student tenant flow to avoid CORS issues
 const currentUser = JSON.parse(localStorage.getItem('user') || "{}");
 const isMainPlatform = window.location.hostname === 'www.theskillgenie.com' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
 const isStudentTenant = currentUser && (currentUser.role === 'student' && (currentUser.tenant || currentUser.college_id || !isMainPlatform));

 const headers = {};
 if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
 if (sessionToken && !isStudentTenant) headers['X-Session-Token'] = sessionToken;

 const res = await fetch(`${API_BASE_URL}/api/individual-roles/user-data/${userId}?userId=${encodeURIComponent(userId || "")}`, {
  headers
 });
 const data = await res.json();
 if (!res.ok || !data.success) throw new Error(data.error || 'Failed to fetch user context');
 const userData = data.data;
 return { 
 user: userData, 
 enrollment: { 
 track: 'roles', 
 bunchName: userData.role, 
 skillSlug: userData.skillSlug,
 experienceLevel: userData.experience_level,
 githubRepoUrl: userData.github_repo_url,
 github_repo_url: userData.github_repo_url,
 invitationStatus: userData.invitation_status,
 invitation_status: userData.invitation_status,
 provisioningStatus: userData.provisioning_status,
 provisioning_status: userData.provisioning_status,
 enrollmentId: userData.enrollment_id
 } 
 };
}

async function loadTasks(role, userId) {
  try {
    // 1. Get user context/enrollment to find the correct role and file
    const ctx = await fetchUserContext(userId);
    const fileName = ctx.enrollment.skillSlug || ctx.enrollment.role || role;
    
    // 2. Fetch the JSON file directly from the frontend public folder
    const url = pub(`${PUBLIC_ROOT}/${fileName}.json`);
    const res = await fetch(url, { cache: "no-store" });
    
    if (!res.ok) {
       console.warn(`Direct fetch failed for ${url}, falling back to API`);
       // Fallback to existing API logic if direct fetch fails
       return await loadTasksFromAPI(role, userId);
    }
    
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
       console.warn(`Direct fetch returned non-JSON for ${url}, falling back to API`);
       return await loadTasksFromAPI(role, userId);
    }
    
    const data = await res.json();
    
    // Process tasks using a robust mapping (similar to techbunch)
    let rawTasks = [];
    if (Array.isArray(data.tasks)) rawTasks = data.tasks;
    else if (Array.isArray(data.projects?.[0]?.tasks)) rawTasks = data.projects[0].tasks;
    else if (Array.isArray(data.topics)) rawTasks = data.topics;
    
    const allTasks = rawTasks.map((task, taskIndex) => ({
      id: task.task_id || `task-${taskIndex}`,
      title: task.title || task.task_title || task.topic || `Task ${taskIndex + 1}`,
      topic: task.topic || task.title || task.task_title || `Topic ${taskIndex + 1}`,
      description: task.description || task.task_description || "",
      subtopics: Array.isArray(task.subtopics) ? task.subtopics : [],
      useCase: task.use_case || task.useCase || "",
      futureUseCase: task.future_use_case || task.futureUseCase || "",
      expectedOutput: task.expected_output || task.expectedOutput || "",
      externalTools: Array.isArray(task.external_tools) ? task.external_tools : [],
      projectName: data.project || data.role || role,
      projectIndex: 0,
      phaseNo: task.phase_no || taskIndex + 1
    }));

    // 3. Check payment status
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const isMainPlatform = window.location.hostname === 'www.theskillgenie.com' || 
                           window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1';
    
    const isStudentTenant = user && (user.role === 'student' && (user.tenant || user.college_id || !isMainPlatform));
    let isPaid = isStudentTenant;
    
    if (!isPaid && userId) {
       const { hasFullAccess } = await import('../utils/paymentStatus');
       isPaid = await hasFullAccess(userId);
    }

    if (!isPaid) return [];
    
    return allTasks;
  } catch (err) {
    console.error("loadTasks error:", err);
    return await loadTasksFromAPI(role, userId);
  }
}

async function loadTasksFromAPI(role, userId) {
 const url = `${API_BASE_URL}/api/individual-roles/tasks/${userId}?userId=${encodeURIComponent(userId || "")}`;
 
 // Check if user is paid
 const user = JSON.parse(localStorage.getItem("user") || "{}");
 const isMainPlatform = window.location.hostname === 'www.theskillgenie.com' || 
                        window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1';
 
 const isStudentTenant = user && (user.role === 'student' && (user.tenant || user.college_id || !isMainPlatform));
 let isPaid = isStudentTenant;
 
 if (!isPaid && userId) {
    const { hasFullAccess } = await import('../utils/paymentStatus');
    isPaid = await hasFullAccess(userId);
 }

 const sessionToken = localStorage.getItem('session_token');
 // Avoid sending X-Session-Token header for student tenant flow
 // Reuse isStudentTenant computed earlier in this function (do not redeclare)

 const headers = {
   'user-id': userId || '',
   'userId': userId || '',
   'user_id': userId || ''
 };
 if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
 if (sessionToken && !isStudentTenant) headers['X-Session-Token'] = sessionToken;

 const res = await fetch(url, {
  headers
 });
 if (!res.ok) throw new Error(`Failed to load tasks for role: ${role}`);
 const data = await res.json();
 if (!data.success || !data.data.tasks) return [];

 // Process tasks from the new data structure
 const allTasks = [];
 data.data.tasks.forEach((task, taskIndex) => {
 allTasks.push({
 id: `task-${taskIndex}`,
 title: task.title || `Task ${taskIndex + 1}`,
 topic: task.title || `Topic ${taskIndex + 1}`,
 description: task.description ||"",
 subtopics: [],
 useCase: task.use_case ||"",
 futureUseCase: task.future_use_case ||"",
 expectedOutput: task.expected_output ||"",
 externalTools: [],
 projectName: data.data.role,
 projectIndex: 0,
 phaseNo: task.phase_no || taskIndex + 1
 });
 });

  // If not paid, don't allow access (no free trial for bootcamp)
  if (!isPaid) {
     return [];
  }

 return allTasks;
}

async function reviewTaskWithAI({ userId, bunchName, experienceLevel, task, docFile, imgFile, comments, githubUrl, submissionType }) {
 const url = `${API_BASE_URL}/api/user-ai-review?userId=${encodeURIComponent(userId || "")}`;
 try {
 const fd = new FormData();
 fd.append("userId", userId);
 fd.append("bunchName", bunchName);
 fd.append("experienceLevel", experienceLevel);
 fd.append("taskId", task.id);
 fd.append("taskTitle", task.title);
 fd.append("expectedOutput", task.expectedOutput ||"");
 fd.append("useCase", task.useCase ||"");
 fd.append("futureUseCase", task.futureUseCase ||"");
 fd.append("subtopics", JSON.stringify(task.subtopics || []));
 fd.append("comments", comments ||"");
 fd.append("submissionType", submissionType ||"files"); //"files" or"github"
 fd.append("githubUrl", githubUrl ||"");
 if (docFile) fd.append("document", docFile);
 if (imgFile) fd.append("image", imgFile);

 const sessionToken = localStorage.getItem('session_token');
 const headers = {};
 if (sessionToken) {
   headers['Authorization'] = `Bearer ${sessionToken}`;
 }

 const res = await fetch(url, { 
   method:"POST", 
   body: fd,
   headers
 });
 const data = await res.json();
 if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
 return data;
 } catch (err) {
 console.error("IndividualRoles reviewTaskWithAI error:", err);
 throw new Error(
 err?.message
 ? `Submission failed: ${err.message}`
 :"Submission failed. Please ensure your work aligns with the task and try again."
 );
 }
}

async function askMentorAPI({ userId, roleName, experienceLevel, task, text, imgFile, history = [] }) {
 const url = `${API_BASE_URL}/api/bootcamp/roles/mentor?userId=${encodeURIComponent(userId || "")}`;
 try {
 const fd = new FormData();
 fd.append("userId", userId);
 fd.append("roleName", roleName);
 fd.append("experienceLevel", experienceLevel);
 fd.append("taskId", task?.id ||"");
 fd.append("taskTitle", task?.title || task?.topic ||"");
 fd.append("context", JSON.stringify({
 expectedOutput: task?.expectedOutput ||"",
 subtopics: task?.subtopics || [],
 description: task?.description ||"",
 }));
 fd.append("message", text ||"");
 fd.append("history", JSON.stringify(history.slice(-10).map(m => ({ role: m.role, text: m.text }))));
 if (imgFile) fd.append("image", imgFile);
 
 const sessionToken = localStorage.getItem('session_token');
 const headers = {};
 if (sessionToken) {
   headers['Authorization'] = `Bearer ${sessionToken}`;
 }

 const res = await fetch(url, { 
   method:"POST", 
   body: fd,
   headers
 });
 const data = await res.json();
 if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
 return data;
 } catch {
 return { ok: true, answer:"Demo mentor: share your exact error or a screenshot.", mock: true };
 }
}

async function visualizeOutputAPI({ userId, bunchName, experienceLevel, task, hint }) {
 const url = `${API_BASE_URL}/api/bootcamp/roles/visualize?userId=${encodeURIComponent(userId || "")}`;
 
 // Add timeout to prevent hanging requests
 const controller = new AbortController();
 const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
 
 try {
 const res = await fetch(url, {
 method:"POST",
 headers: {
  "Content-Type":"application/json"
 },
 body: JSON.stringify({
 userId,
 roleName: bunchName,
 experienceLevel,
 task,
 hint: hint ||"",
 taskDescription: task?.description || "",
 taskSubtopics: task?.subtopics || [],
 taskUseCase: task?.useCase || "",
 }),
 signal: controller.signal,
 });

 clearTimeout(timeoutId);

 // Handle 504 Gateway Timeout specifically
 if (res.status === 504) {
 throw new Error("The visualization is taking longer than expected. This might be due to high server load. Please try again in a moment.");
 }

 // Check if response is HTML (error page) instead of JSON
 const contentType = res.headers.get("content-type");
 if (!contentType || !contentType.includes("application/json")) {
 const text = await res.text();
 throw new Error(`Server returned HTML instead of JSON. Status: ${res.status}. Response: ${text.substring(0, 200)}...`);
 }

 const data = await res.json();
 if (!res.ok || !data.ok) {
 // Provide more helpful error messages
 if (res.status === 504) {
 throw new Error("The visualization request timed out. Please try again with a simpler request or try again later.");
 }
 throw new Error(data.error || `HTTP ${res.status}`);
 }
 return data;
 } catch (err) {
 clearTimeout(timeoutId);
 // Handle abort (timeout) errors
 if (err.name === 'AbortError') {
 throw new Error("Request timed out. The visualization is taking too long. Please try again.");
 }
 // Re-throw other errors
 throw err;
 }
}

async function generateTopicExplanationAPI({ userId, bunchName, experienceLevel, task, language = 'en' }) {
 const url = `${API_BASE_URL}/api/bootcamp/roles/explain-topic`;
 
 // Add timeout to prevent hanging requests
 const controller = new AbortController();
 const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
 
 try {
 const res = await fetch(url, {
 method:"POST",
 headers: {
  "Content-Type":"application/json",
  "user-id": userId
 },
 body: JSON.stringify({
 userId, roleName: bunchName, experienceLevel, task, language,
 taskDescription: task?.description || "",
 taskSubtopics: task?.subtopics || [],
 taskUseCase: task?.useCase || "",
 taskFutureUseCase: task?.futureUseCase || "",
 taskExpectedOutput: task?.expectedOutput || "",
 }),
 signal: controller.signal,
 });
 
 clearTimeout(timeoutId);

 // Handle 504 Gateway Timeout specifically
 if (res.status === 504) {
 throw new Error("The explanation is taking longer than expected. This might be due to high server load. Please try again in a moment.");
 }

 // Check if response is HTML (error page) instead of JSON
 const contentType = res.headers.get("content-type");
 if (!contentType || !contentType.includes("application/json")) {
 const text = await res.text();
 throw new Error(`Server returned HTML instead of JSON. Status: ${res.status}. Response: ${text.substring(0, 200)}...`);
 }

 const data = await res.json();
 if (!res.ok || !data.ok) {
 // Provide more helpful error messages
 if (res.status === 504) {
 throw new Error("The explanation request timed out. Please try again with a simpler topic or try again later.");
 }
 throw new Error(data.error || `HTTP ${res.status}`);
 }
 return data;
 } catch (err) {
 clearTimeout(timeoutId);
 // Handle abort (timeout) errors
 if (err.name === 'AbortError') {
 throw new Error("Request timed out. The explanation is taking too long. Please try again.");
 }
 // Re-throw other errors
 throw err;
 }
}

async function generateVideoAPI({ userId, task, courseType, experienceLevel }) {
 const url = `${API_BASE_URL}/api/video/generate?userId=${encodeURIComponent(userId || "")}`;
 const res = await fetch(url, {
 method:"POST",
 headers: {
  "Content-Type":"application/json"
 },
 body: JSON.stringify({
 userId,
 courseType,
 experienceLevel,
 duration: 75,
 task: {
 id: task?.id ||"",
 title: task?.title ||"",
 topic: task?.topic ||"",
 description: task?.description ||"",
 useCase: task?.useCase ||"",
 futureUseCase: task?.futureUseCase ||"",
 expectedOutput: task?.expectedOutput ||"",
 projectName: task?.projectName ||"",
 phaseNo: task?.phaseNo || 0
 }
 }),
 });

 const contentType = res.headers.get("content-type");
 if (!contentType || !contentType.includes("application/json")) {
 const text = await res.text();
 throw new Error(`Server returned HTML instead of JSON. Status: ${res.status}. Response: ${text.substring(0, 200)}...`);
 }

 const data = await res.json();
 if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
 return data;
}

/* -------------------------------------------------------------------------- */
/* component */
/* -------------------------------------------------------------------------- */
export default function IndividualRoles() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notAllowed, setNotAllowed] = useState("");
  const [userId, setUserId] = useState(null);

  const [tasks, setTasks] = useState([]);
  const [completedTaskIds, setCompletedTaskIds] = useState([]);
  const [viewIndex, setViewIndex] = useState(0);

  // Derived state (Moved to top to prevent ReferenceError)
  const totalTasks = tasks.length;
  const doneSet = useMemo(() => new Set(completedTaskIds), [completedTaskIds]);
  const completedCount = useMemo(() => {
    let c = 0; 
    for (let i = 0; i < totalTasks; i++) { 
      const id = tasks[i]?.id; 
      if (id && doneSet.has(id)) c++; else break; 
    } 
    return c;
  }, [doneSet, tasks, totalTasks]);
  const unlockedIndex = clamp(completedCount, 0, Math.max(0, totalTasks - 1));
  const percent = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
  const viewingTask = viewIndex != null ? tasks[viewIndex] : null;

  const [userName, setUserName] = useState("");
  const [track, setTrack] = useState("");
  const [bunchName, setBunchName] = useState("");
  const [enrollment, setEnrollment] = useState(null);
  const technology = bunchName || "Role";
  const [experienceLevel, setExperienceLevel] = useState("");
  const [githubRepoUrl, setGithubRepoUrl] = useState("");
  const [invitationStatus, setInvitationStatus] = useState("");
  const [githubStatus, setGithubStatus] = useState(null);

  const syncInvitationStatus = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/bootcamp/sync-invitation/${userId}`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        setInvitationStatus(data.invitationStatus || data.invitation_status || "");
        setGithubRepoUrl(data.githubRepoUrl || data.github_repo_url || "");
        setGithubStatus({
          repo_url: data.githubRepoUrl || data.github_repo_url || "",
          invitation_status: data.invitationStatus || data.invitation_status || "",
          provisioning_status: data.provisioningStatus || data.provisioning_status || (data.githubRepoUrl || data.github_repo_url ? "READY" : "PENDING")
        });
      }
    } catch (err) {
      console.error("Failed to sync invitation status", err);
    }
  };

  const [reviewStateById, setReviewStateById] = useState({});
  const [certificateEligible, setCertificateEligible] = useState(false);
  
  // Listen for progress updates from other components
  useEffect(() => {
    const handleUpdate = (e) => {
      if (e.detail && e.detail.userId === userId) {
        console.log("📈 Progress update detected via event, refreshing state...");
        const { completedTaskIds: nextIds, reviewStateById: nextReviews } = readProgress(userId, bunchName);
        setCompletedTaskIds(nextIds);
        setReviewStateById(nextReviews);
      }
    };
    window.addEventListener('skillgenProgressUpdated', handleUpdate);
    return () => window.removeEventListener('skillgenProgressUpdated', handleUpdate);
  }, [userId, bunchName]);

  // Task completion tracking
  const { markTaskCompleted } = useTaskCompletion(userId, 'individual');

  const [modalTab, setModalTab] = useState("overview");
 const [showModal, setShowModal] = useState(false);
 const [showMentor, setShowMentor] = useState(false);
 const [mentorMessages, setMentorMessages] = useState([]);
 
 // 🎯 LLM Trial state
 const [llmTrialStatus, setLlmTrialStatus] = useState(null);
 const [showTrialModal, setShowTrialModal] = useState(false);
 const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

 // Voice Assistant
 const [vaInitialMessage, setVaInitialMessage] = useState("");
 const {
   isVoiceAssistantOpen,
   openVoiceAssistant,
   closeVoiceAssistant,
   handleVoiceCommand,
   handleVoiceNavigation
 } = useVoiceAssistant();

 // Helper to open Voice Assistant with subtopic explanation
 const explainWithAI = async (subtopic) => {
   try {
     setVaInitialMessage(`Generating explanation for: ${subtopic}...`);
     openVoiceAssistant();
     
     // Fetch detailed explanation for the subtopic
     const res = await fetch(`${API_BASE_URL}/chat`, {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
         'user-id': userId
       },
       body: JSON.stringify({
         message: `Explain the subtopic "${subtopic}" for the topic "${tasks[viewIndex]?.title || tasks[viewIndex]?.topic}" in ${bunchName || 'the role'}. Provide a detailed explanation that I can read.`,
         langMode: "fixed",
         langCode: "en-US",
         userId: userId
       })
     });
     
     if (res.ok) {
       const data = await res.json();
       setVaInitialMessage(data.reply);
     } else {
       setVaInitialMessage(`Sorry, I couldn't generate an explanation for "${subtopic}" right now. Please try again.`);
     }
   } catch (err) {
     console.error("Failed to explain subtopic:", err);
     setVaInitialMessage("Error connecting to AI Assistant.");
   }
 };

 const [visualizing, setVisualizing] = useState(false);
 const [visualGeneratedAt, setVisualGeneratedAt] = useState(null);
 const [visualHint, setVisualHint] = useState("");

 const [explanationUsage, setExplanationUsage] = useState(null);
 const [videoUsage, setVideoUsage] = useState(null);
 const videoStatusRef = useRef("");

 const [explanationLoading, setExplanationLoading] = useState(false);
 const [topicExplanation, setTopicExplanation] = useState(null);
 const [explanationLanguage] = useState('en'); 
 const [videoGenerating, setVideoGenerating] = useState(false);
 const [videoUrl, setVideoUrl] = useState(null);
 const [videoStatus, setVideoStatus] = useState(""); //"generating","queued","ready","error"
 const [videoMessage, setVideoMessage] = useState("");
 const [videoError, setVideoError] = useState("");
 const videoPollRef = useRef(null);
 const videoBaseUrlRef = useRef(null);
 const videoPollInFlightRef = useRef(false);
 const videoPollStartedAtRef = useRef(null);
 const videoErrorRetriesRef = useRef(0);

 const [visualUrl, setVisualUrl] = useState("");
  const openTask = (idx, tab ="overview") => { setViewIndex(idx); setModalTab(tab); setShowModal(true); setVisualUrl(""); };
  const openCurrentTask = (tab ="overview") => openTask(unlockedIndex, tab);

  useEffect(() => {
    const id =
    localStorage.getItem("user_id") ||
    (() => { try { const u = JSON.parse(localStorage.getItem("user") ||"{}"); return u.userId || u.user_id ||""; } catch { return""; } })();
    setUserId(id ||"");
  }, []);

 // ✅ Check payment status before loading dashboard
 useEffect(() => {
   let alive = true;
   (async () => {
     if (userId === null || !userId) return;
      
     try {
       // ✅ NEW: Student Tenant Access check first
       const user = JSON.parse(localStorage.getItem("user") || '{}');
       const isMainPlatform = window.location.hostname === 'theskillgenie.com' || 
                              window.location.hostname === 'www.theskillgenie.com' || 
                              window.location.hostname === 'localhost' ||
                              window.location.hostname === '127.0.0.1';

       if (user && (user.role === 'student' && (user.tenant || user.college_id || !isMainPlatform))) {
         console.log(`[IndividualRoles] Full access granted to Student Tenant: ${userId}`);
         setLoading(false);
         setNotAllowed("");
         return;
       }

       const hasAccess = await hasFullAccess(userId);
 if (!hasAccess && alive) {
 const proceed = await redirectToPaymentIfNeeded(userId, navigate);
 if (!proceed && alive) {
 setLoading(false);
 setNotAllowed("Premium subscription required to access this dashboard.");
 return;
 }
 }
 } catch (err) {
 console.error("Payment check failed:", err);
 // Continue loading - backend will also check
 }
 })();
 return () => { alive = false; };
 }, [userId, navigate]);

 useEffect(() => {
 let alive = true;
 (async () => {
 try {
 if (userId === null) return;
 if (!userId) { if (alive) { setLoading(false); setNotAllowed("Missing login."); } return; }

 const ctx = await fetchUserContext(userId);
 if (alive) {
   setUserName(ctx.user?.name || userId);
   setTrack(ctx.enrollment?.track || "roles");
   setBunchName(ctx.enrollment?.bunchName || "");
   setEnrollment(ctx.enrollment);
   setExperienceLevel(ctx.enrollment?.experienceLevel || "");
   setGithubRepoUrl(ctx.enrollment?.githubRepoUrl || ctx.enrollment?.github_repo_url || "");
   setInvitationStatus(ctx.enrollment?.invitationStatus || ctx.enrollment?.invitation_status || "");
   setGithubStatus({
     repo_url: ctx.enrollment?.githubRepoUrl || ctx.enrollment?.github_repo_url || "",
     invitation_status: ctx.enrollment?.invitationStatus || ctx.enrollment?.invitation_status || "",
     provisioning_status: ctx.enrollment?.provisioningStatus || ctx.enrollment?.provisioning_status || (ctx.enrollment?.githubRepoUrl || ctx.enrollment?.github_repo_url ? "READY" : "PENDING")
   });
 
 // Store enrollment data in localStorage for CertificatePage access
 localStorage.setItem(`enrollment_${userId}`, JSON.stringify({
 bunchName: ctx.enrollment?.bunchName ||"",
 technology: ctx.enrollment?.bunchName ||"",
 track: ctx.enrollment?.track ||"roles",
 experienceLevel: ctx.enrollment?.experienceLevel ||""
 }));
 
 console.log('💾 Enrollment data stored in localStorage:', {
 userId,
 enrollment: ctx.enrollment
 });
 }

 const t = await loadTasks(ctx.enrollment.skillSlug || ctx.enrollment.bunchName, userId);

 const store = readProgress(userId, ctx.enrollment.bunchName);
 if (alive) {
   setTasks(t);
   setCompletedTaskIds(store.completedTaskIds);
   setReviewStateById(store.reviewStateById || {});
   setMentorMessages(readChat(userId, ctx.enrollment.bunchName));
   setCertificateEligible(t.length > 0 && (store.completedTaskIds?.length || 0) >= t.length);

   // Set viewIndex to first incomplete task
   const doneSet = new Set(store.completedTaskIds);
   let firstIncomplete = 0;
   for (let i = 0; i < t.length; i++) {
     if (!doneSet.has(t[i].id)) {
       firstIncomplete = i;
       break;
     }
   }
   setViewIndex(firstIncomplete);
          
   // Sync current progress to backend for certificate tracking
   syncProgressToBackend(userId, ctx.enrollment.bunchName, store.completedTaskIds, t.length);
          
   setLoading(false);
   setNotAllowed("");
 }
 } catch (err) {
 if (alive) { setNotAllowed(String(err?.message ||"Access restricted")); setLoading(false); }
 }
 })();
 return () => { alive = false; };
 }, [userId]);

 // ✅ Check LLM Trial Status on component mount
 useEffect(() => {
 if (userId) {
 (async () => {
 const status = await checkLLMTrialStatus(userId);
 setLlmTrialStatus(status);
 console.log('🎯 LLM Trial Status (IndividualRoles):', status);
 
 // Auto-show welcome modal for new trial users (first time only)
 if (status?.hasActiveTrial && status?.source === 'trial') {
 const welcomeShownKey = `llm_trial_welcome_shown_${userId}`;
 const welcomeShown = localStorage.getItem(welcomeShownKey);
 if (!welcomeShown) {
 setShowTrialModal(true);
 localStorage.setItem(welcomeShownKey, 'true');
 }
 }
 })();
 }
 }, [userId]);

 const cacheBustUrl = (baseUrl) => `${baseUrl}${baseUrl.includes("?") ?"&" :"?"}v=${Date.now()}`;
 const stopVideoPolling = () => {
 if (videoPollRef.current) {
 clearInterval(videoPollRef.current);
 videoPollRef.current = null;
 }
 videoPollInFlightRef.current = false;
 videoPollStartedAtRef.current = null;
 };
 const startVideoPolling = (baseUrl, originProbeUrl) => {
 stopVideoPolling();
 videoBaseUrlRef.current = baseUrl;
 videoPollStartedAtRef.current = Date.now();

 const checkAvailability = async () => {
 // Use ref to track status to avoid closure issues
 const currentStatus = videoStatusRef.current;
 if (!videoBaseUrlRef.current || currentStatus ==="ready" || currentStatus ==="error") return;
 if (videoPollInFlightRef.current) return;
 videoPollInFlightRef.current = true;

 // Use backend endpoint to check if video exists (no CORS issues)
 const baseUrl = videoBaseUrlRef.current;
 if (!baseUrl) {
 videoPollInFlightRef.current = false;
 return;
 }
 
 try {
 // Check via backend endpoint - this avoids CORS issues
 const checkUrl = `${API_BASE_URL}/api/video/check-exists?url=${encodeURIComponent(baseUrl)}`;
 const res = await fetch(checkUrl, {
 method:"GET",
 cache:"no-store",
 });
 
 if (!res.ok) {
 videoPollInFlightRef.current = false;
 return;
 }
 
 const data = await res.json();
 
 if (data.ok && data.exists && data.status === 'ready') {
 // Video is ready!
 stopVideoPolling();
 const proxiedBaseUrl = getProxiedVideoUrl(baseUrl);
 const finalUrl = cacheBustUrl(proxiedBaseUrl);
 videoStatusRef.current ="ready";
 setVideoUrl(finalUrl);
 setVideoStatus("ready");
 setVideoMessage("Your tutorial video is ready to watch!");
 setVideoError("");
 videoErrorRetriesRef.current = 0;
 console.log("[Video Polling] Video confirmed ready via backend:", finalUrl);
 return;
 } else if (data.ok === false || data.error || data.status === 'failed') {
 // Video generation failed or error occurred
 stopVideoPolling();
 videoStatusRef.current ="error";
 setVideoStatus("error");
 const errorMessage = data.error || data.message ||"Video generation failed. Please try regenerating.";
 setVideoError(errorMessage);
 setVideoMessage("");
 console.error("[Video Polling] Video generation failed:", {
 error: data.error,
 message: data.message,
 status: data.status
 });
 return;
 } else {
 // Video not ready yet, continue polling
 console.debug("[Video Polling] Video not ready yet, status:", data.status,"exists:", data.exists);
 }
 } catch (err) {
 // Log errors but don't stop polling immediately - video might still be processing
 console.warn("[Video Polling] Check failed:", err?.message || err);
 // If we've been polling for more than 3 minutes and getting errors, it might be a real issue
 const startedAt = videoPollStartedAtRef.current;
 if (startedAt && Date.now() - startedAt > 3 * 60 * 1000) {
 console.error("[Video Polling] Persistent errors after 3 minutes - video generation may have failed");
 }
 } finally {
 videoPollInFlightRef.current = false;
 }

 const startedAt = videoPollStartedAtRef.current;
 // Reduce timeout to 5 minutes since video generation should complete faster
 if (startedAt && Date.now() - startedAt > 5 * 60 * 1000) {
 stopVideoPolling();
 videoStatusRef.current ="error";
 setVideoStatus("error");
 setVideoError("Video generation timed out after 5 minutes. The video may have failed to generate. Please try regenerating.");
 setVideoMessage("");
 console.warn("[Video Polling] Timeout after 5 minutes");
 }
 };

 checkAvailability();
 videoPollRef.current = setInterval(checkAvailability, 12000);
 };
 useEffect(() => {
 return () => {
 stopVideoPolling();
 };
 }, []);

 const handleVideoLoaded = () => {
 console.log("[Video Player] Video loaded successfully");
 stopVideoPolling();
 videoStatusRef.current ="ready";
 setVideoStatus("ready");
 setVideoMessage("Your tutorial video is ready to watch!");
 setVideoError("");
 videoErrorRetriesRef.current = 0;
 };
 const handleVideoLoadError = (e) => {
 const currentStatus = videoStatusRef.current || videoStatus;
 const currentUrl = videoUrl;
 const base = videoBaseUrlRef.current;
 
 // Don't fallback to direct CloudFront URL as it will cause CORS errors
 // The proxy URL should work - if it doesn't, the video might still be processing
 // Continue with normal error handling
 
 if (currentStatus ==="queued" || currentStatus ==="generating") {
 // Video is still rendering, this is expected - don't show error yet
 // Silently ignore errors during queued/generating status
 return;
 }
 
 // Only log errors if video is not in queued/generating status
 console.warn("[Video Player] Video load error:", {
 status: currentStatus,
 url: currentUrl,
 baseUrl: base,
 error: e?.target?.error,
 networkState: e?.target?.networkState,
 readyState: e?.target?.readyState,
 retries: videoErrorRetriesRef.current
 });
 
 // If status is"ready" but video fails, it might be a false positive from polling
 // Reset to"queued" and let polling continue
 if (currentStatus ==="ready" && videoErrorRetriesRef.current < 3) {
 console.log("[Video Player] Video marked ready but failed to load, resetting to queued");
 videoStatusRef.current ="queued";
 setVideoStatus("queued");
 setVideoMessage("Video is still processing, please wait...");
 // Don't increment retry count for this case
 return;
 }
 
 if (base && videoErrorRetriesRef.current < 5) {
 videoErrorRetriesRef.current += 1;
 console.log(`[Video Player] Retrying video load (attempt ${videoErrorRetriesRef.current}/5)`);
 setTimeout(() => {
 // Always use proxy URL to avoid CORS errors
 const retryUrl = cacheBustUrl(getProxiedVideoUrl(base));
 console.log(`[Video Player] Retrying with proxy URL: ${retryUrl}`);
 setVideoUrl(retryUrl);
 }, 3000 * videoErrorRetriesRef.current);
 } else {
 console.error("[Video Player] Video failed to load after retries", {
 finalUrl: currentUrl,
 baseUrl: base,
 status: currentStatus
 });
 setVideoStatus("error");
 setVideoError(`Video failed to load. The video may not be available yet or the URL may be incorrect. Please try regenerating the video.`);
 // Reset retry counter for next attempt
 videoErrorRetriesRef.current = 0;
 }
 };


 // Voice command handlers
 const onVoiceCommand = (command) => {
 const result = handleVoiceCommand(command);
 
 switch (result.type) {
 case 'open-mentor':
 setShowMentor(true);
 break;
 case 'open-video':
 openCurrentTask('video');
 break;
 case 'open-resources':
 openCurrentTask('resources');
 break;
 case 'open-submit':
 openCurrentTask('submit');
 break;
 case 'open-feedback':
 openCurrentTask('feedback');
 break;
 case 'open-visualize':
 openCurrentTask('visualize');
 break;
 case 'start-task':
 if (tasks.length > 0) {
 openCurrentTask('overview');
 }
 break;
 case 'next-task':
 if (viewIndex !== null && viewIndex < tasks.length - 1) {
 openTask(viewIndex + 1);
 }
 break;
 case 'previous-task':
 if (viewIndex !== null && viewIndex > 0) {
 openTask(viewIndex - 1);
 }
 break;
 case 'get-help':
 setShowMentor(true);
 break;
 case 'explain-current':
 if (viewingTask) {
 generateTopicExplanation();
 }
 break;
 }
 };

 const onVoiceNavigate = (path) => {
 const result = handleVoiceNavigation(path);
 if (result.type === 'navigate') {
 navigate(result.path);
 }
 };

 const sendMentorMessage = async ({ text, imgFile }) => {
  if (!text && !imgFile) return;
  const you = { id: `u-${Date.now()}`, role: "user", text, imagePreview: imgFile ? URL.createObjectURL(imgFile) : null, when: new Date().toISOString() };
  const thinking = { id: `t-${Date.now()}`, role: "assistant", text: "Thinking…", typing: true, when: new Date().toISOString() };
  const add = (arr) => { setMentorMessages(arr); writeChat(userId, bunchName, arr); };

  const now = [...mentorMessages, you, thinking];
  add(now);
  const task = tasks[unlockedIndex];

  // Enhance text with technology context if it's a general question
  let enhancedText = text;
  if (text && text.length < 100 && !text.toLowerCase().includes(technology.toLowerCase())) {
    enhancedText = `Regarding ${technology}: ${text}`;
  }

  const res = await askMentorAPI({ userId, roleName: bunchName, experienceLevel, task, text: enhancedText, imgFile, history: mentorMessages });
  const final = [...now.filter(m => !m.typing), { id: `a-${Date.now()}`, role: "assistant", text: res.answer || "I'm here to help.", when: new Date().toISOString() }];
  add(final);
};

 const handleVisualize = async () => {
 if (!viewingTask) return;
 setVisualizing(true);
 setVisualUrl("");
 setVisualGeneratedAt(null);
 try {
 console.log('🎨 Generating visualization for:', viewingTask.topic);
 const data = await visualizeOutputAPI({
 userId, bunchName, experienceLevel, task: viewingTask, hint: visualHint
 });
 
 const imageUrl = data.imageUrl || data.visualUrl || data.imageData ||"";
 if (imageUrl) {
 setVisualUrl(imageUrl);
 setVisualGeneratedAt(new Date());
 setModalTab("visualize");
 console.log('✅ Visualization generated successfully');
 } else {
 throw new Error("Visualization service did not return an image URL");
 }
 } catch (e) {
 console.error('❌ Visualization error:', e);
 const errorMsg = e.message ||"Failed to generate visualization";
 alert(`❌ ${errorMsg}\n\nPlease try again. If the issue persists, check your internet connection and OpenAI API configuration.`);
 } finally {
 setVisualizing(false);
 }
 };

 const generateTopicExplanation = async () => {
 if (!viewingTask) return;
 setExplanationLoading(true);
 setTopicExplanation(null);
 try {
 const data = await generateTopicExplanationAPI({
 userId, bunchName, experienceLevel,
 task: viewingTask,
 language: explanationLanguage
 });
 // Backend returns: { ok: true, explanation: { explanation: explanationData, ..., usage: {...} } }
 // TaskModal receives: explanation={{ explanation: topicExplanation }}
 // TaskModal accesses: explanation.explanation.topicOverview
 // So we need to set topicExplanation to the inner explanationData
 const explanationData = data.explanation?.explanation || data.explanation;
 setTopicExplanation(explanationData);
 if (data.explanation?.usage) {
   setExplanationUsage(data.explanation.usage);
 }
 setModalTab("resources");
 } catch (e) {
 console.error("Topic explanation error:", e);
 const errorMessage = e.message.includes("HTML instead of JSON")
 ?"Backend server is not running or endpoint not found. Please ensure the backend server is running on port 5000."
 : e.message ||"Failed to generate topic explanation. Please try again.";
 alert(errorMessage);
 } finally { setExplanationLoading(false); }
 };

 const generateVideo = async () => {
 if (!viewingTask) return;
 
 const confirmed = window.confirm(
 `Generate tutorial video for"${viewingTask.topic}"?\n\n` +
 `This will create a comprehensive video tutorial covering:\n` +
 `• ${viewingTask.subtopics?.length || 0} subtopics\n` +
 `• Practical examples and code\n` +
 `• Real-world applications\n` +
 `• Best practices\n\n` +
 `Estimated time: 1-2 minutes\n` +
 `Video length: 60-90 seconds`
 );
 
 if (!confirmed) return;
 
 setVideoGenerating(true);
 videoStatusRef.current ="generating";
 setVideoStatus("generating");
 setVideoMessage("");
 setVideoError("");
 stopVideoPolling();
 setVideoUrl(null);
 
 try {
 const data = await generateVideoAPI({
 userId,
 task: viewingTask,
 courseType:"roles",
 experienceLevel
 });
 
 const videoPath = data.videoUrl || data.expectedUrl;
 if (!videoPath) {
 throw new Error("Video generation service did not return a video URL.");
 }
 const absoluteUrl = videoPath.startsWith("http")
 ? videoPath
 : `${API_BASE_URL}${videoPath.startsWith("/") ? videoPath : `/${videoPath}`}`;

 // Use proxy URL for CloudFront videos to avoid CORS issues
 const proxiedAbsoluteUrl = getProxiedVideoUrl(absoluteUrl);
 const originProbeUrl = (data.originUrl && data.originUrl.startsWith("http"))
 ? getProxiedVideoUrl(data.originUrl)
 : null;

 if (data.queued) {
 videoStatusRef.current ="queued";
 setVideoStatus("queued");
 setVideoMessage("Rendering video… This usually takes 1-3 minutes. We'll refresh automatically.");
 // Don't set video URL yet - wait until backend confirms it's ready
 // This prevents the video element from trying to load a non-existent video
 setVideoUrl(null);
 // Start polling backend endpoint to detect when video is ready (no CORS issues)
 startVideoPolling(absoluteUrl, originProbeUrl);
 setModalTab("video");
 console.log("[Video Generation] Video queued, polling backend to detect when ready:", absoluteUrl);
 alert(
 `🎬 Video rendering started for"${viewingTask.topic}".\n\n` +
 `Hang tight for ~2 minutes – the player will update automatically once the final MP4 is ready.`
 );
 return;
 }

 stopVideoPolling();
 const readyUrl = cacheBustUrl(proxiedAbsoluteUrl);
 videoStatusRef.current ="ready";
 setVideoUrl(readyUrl);
 setVideoStatus("ready");
 if (data.usage) {
   setVideoUsage(data.usage);
 }
 setVideoMessage("Your tutorial video is ready to watch!");
 setVideoError("");
 setModalTab("video");
 console.log("[Video Generation] Video ready immediately:", readyUrl);
 
 } catch (err) {
 console.error("Video generation failed:", err);
 
 let errorMessage ="Failed to generate video. Please try again.";
 
 if (err.message.includes("HTML instead of JSON")) {
 errorMessage ="❌ Backend server is not running or Python dependencies not installed.\n\nPlease ensure:\n• Backend server is running on port 5000\n• Python dependencies are installed\n• All required packages are available";
 } else if (err.message.includes("timeout")) {
 errorMessage ="⏱️ Video generation is taking longer than expected.\n\nThis can happen with complex topics. Please try again.";
 } else if (err.message.includes("Missing required")) {
 errorMessage ="❌ Missing required information.\n\nPlease ensure the task has a valid topic and all required fields are filled.";
 } else if (err.message.includes("Video generation service")) {
 errorMessage ="🔧 Video generation service is temporarily unavailable.\n\nPlease try again in a few minutes.";
 } else if (err.message) {
 errorMessage = `❌ ${err.message}`;
 }
 
 alert(errorMessage);
 videoStatusRef.current ="error";
 setVideoStatus("error");
 setVideoError(errorMessage);
 } finally {
 setVideoGenerating(false);
 }
 };

 if (loading || userId === null) return <Screen><div className="text-primary">Loading Individual Roles…</div></Screen>;
 if (notAllowed) return <Screen><Denied text={notAllowed} /></Screen>;

 return (
 <div className="min-h-screen bg-bgMain text-body transition-colors duration-300">
 {/* header */}
 <header className="sticky top-0 z-10 border-b border-secondary/30 bg-bgHeaderFooter/90 backdrop-blur transition-colors duration-300">
 <div className="mx-auto max-w-7xl px-4 py-4 flex items-center justify-between">
 <div>
 <h1 className="text-xl md:text-2xl font-extrabold text-primary tracking-tight flex items-center gap-2">
 <ClipboardDocumentListIcon className="h-6 w-6 text-primary" /> {bunchName ? `${bunchName} • Individual Roles` :"Individual Roles"}
 </h1>
 <p className="text-sm text-muted flex items-center gap-2">
 <span>Hi {userName}</span> <span>👋</span> <span>Welcome back.</span>
 </p>
 <p className="mt-1 text-xs text-muted">
 <span className="mr-3">User ID: <span className="text-primary">{userId ||"-"}</span></span>
 <span className="mr-3">Track: <span className="text-primary capitalize">{track ||"-"}</span></span>
 <span>Experience: <span className="text-primary">{experienceLevel ||"-"}</span></span>
 </p>
 <div className="mt-2">
 <SubscriptionStatus />
 </div>
 </div>
 <div className="hidden sm:flex items-center gap-3">
 <QuickLink label="Voice Assistant" icon={<SpeakerWaveIcon className="h-4 w-4" />} onClick={openVoiceAssistant} />
 <QuickLink label="Certificate" icon={<TrophyIcon className="h-4 w-4" />} onClick={() => navigate(`/bootcamp/certificate`)} />
 <QuickLink 
 label={llmTrialStatus?.hasActiveTrial ? `AI Mentor (${llmTrialStatus?.daysRemaining}d left)` :"AI Mentor"} 
 icon={<ChatBubbleLeftRightIcon className="h-4 w-4" />} 
 onClick={() => {
 if (llmTrialStatus?.hasActiveTrial) {
 setShowMentor(true);
 } else if (llmTrialStatus && llmTrialStatus.daysRemaining <= 0) {
 setShowTrialExpiredModal(true);
 } else {
 setShowTrialModal(true);
 }
 }}
 />
 </div>
 </div>
 </header>

  {/* main */}
  <main className="mx-auto max-w-7xl px-4 py-6 space-y-6">
    {/* Voice Assistant & Quick Actions */}
    <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
      <ActionCard
        title="Voice Assistant"
        subtitle="Interactive AI-powered voice guidance for your tasks"
        icon={<SpeakerWaveIcon className="h-6 w-6 text-primary" />}
        onClick={openVoiceAssistant}
      />
      <ActionCard
        title="Certificate"
        subtitle="View requirements or download your earned certificate"
        icon={<TrophyIcon className="h-6 w-6 text-primary" />}
        onClick={() => navigate('/bootcamp/certificate')}
      />
      <ActionCard
        title="AI Mentor"
        subtitle="Get personalized help and guidance from our AI expert"
        icon={<ChatBubbleLeftRightIcon className="h-6 w-6 text-primary" />}
        onClick={() => {
          if (llmTrialStatus?.hasActiveTrial) {
            setShowMentor(true);
          } else if (llmTrialStatus && llmTrialStatus.daysRemaining <= 0) {
            setShowTrialExpiredModal(true);
          } else {
            setShowTrialModal(true);
          }
        }}
      />
    </section>

    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sidebar: Task List */}
      <aside className="lg:col-span-1 space-y-4">
        <div className={`p-4 rounded-2xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-white border-secondary/40 shadow-sm"}`}>
          <h3 className={`font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>Course Roadmap</h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
            {tasks.map((t, idx) => {
              const isLocked = idx > unlockedIndex;
              const isCompleted = completedTaskIds.includes(t.id);
              const isActive = viewIndex === idx;
              
              return (
                <button
                  key={t.id}
                  disabled={isLocked}
                  onClick={() => setViewIndex(idx)}
                  className={`w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3 ${
                    isActive 
                      ? "border-primary bg-primary/10" 
                      : isLocked 
                        ? "opacity-50 cursor-not-allowed border-transparent" 
                        : isDark ? "border-accentGray/10 hover:border-accentGray/30" : "border-gray-100 hover:border-primary/30"
                  }`}
                >
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                    isCompleted ? "bg-green-500/10 text-green-500" : isActive ? "bg-primary text-white" : "bg-secondary/10 text-muted"
                  }`}>
                    {isCompleted ? <CheckCircleIconSolid className="h-5 w-5" /> : <span className="text-sm font-bold">{idx + 1}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-bold truncate ${isActive ? "text-primary" : isDark ? "text-white" : "text-gray-900"}`}>{t.title || t.topic}</p>
                    <p className="text-xs text-muted truncate">{isLocked ? "Locked" : isCompleted ? "Completed" : "In Progress"}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      {/* Main Content: 4 Sections */}
      <div className="lg:col-span-3 space-y-6">
        <div className={`p-6 rounded-2xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-white border-secondary/40 shadow-sm"}`}>
          {/* Header & Tabs */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted font-bold">Task {viewIndex + 1} of {totalTasks}</p>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{tasks[viewIndex]?.title || tasks[viewIndex]?.topic}</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <TabButton active={modalTab === "overview"} onClick={() => setModalTab("overview")}>Overview</TabButton>
              <TabButton active={modalTab === "resources"} onClick={() => setModalTab("resources")}>AI Resources</TabButton>
              <TabButton active={modalTab === "video"} onClick={() => setModalTab("video")}>Video Tutorial</TabButton>
              <TabButton active={modalTab === "submit"} onClick={() => setModalTab("submit")}>Submit AI Review</TabButton>
              <TabButton active={modalTab === "feedback"} onClick={() => setModalTab("feedback")}>Feedback</TabButton>
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]">
            {tasks[viewIndex] ? (
              <>
                {modalTab === "overview" && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <Field label="Description">
                          <p className={`text-sm leading-relaxed ${isDark ? "text-gray-300" : "text-gray-600"}`}>{tasks[viewIndex].description}</p>
                        </Field>
                        {tasks[viewIndex].useCase && (
                          <Info label="Use Case">
                            <DocumentTextIcon className="h-4 w-4 text-primary" /> {tasks[viewIndex].useCase}
                          </Info>
                        )}
                      </div>
                      <div className="space-y-4">
                        {tasks[viewIndex].expectedOutput && (
                          <Info label="Expected Output">
                            <CheckCircleIcon className="h-4 w-4 text-primary" /> {tasks[viewIndex].expectedOutput}
                          </Info>
                        )}
                        {tasks[viewIndex].subtopics?.length > 0 && (
                          <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/5" : "bg-gray-50"}`}>
                            <p className="text-sm font-bold mb-2">Key Topics:</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {tasks[viewIndex].subtopics.map((s, i) => (
                                <button
                                  key={i}
                                  onClick={() => explainWithAI(s)}
                                  className="px-3 py-1.5 bg-primary/5 hover:bg-primary/20 border border-primary/20 rounded-lg text-sm text-primary font-medium flex items-center gap-2 transition-all group text-left"
                                >
                                  {s}
                                  <ChatBubbleLeftRightIcon className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                                </button>
                              ))}
                            </div>
                            <p className="text-[10px] text-muted mt-3">Click on any subtopic to get a detailed explanation from our AI Assistant.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {modalTab === "resources" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
                    <div className="text-center py-12">
                      <DocumentTextIcon className="h-12 w-12 text-primary/20 mx-auto mb-4" />
                      <p className="text-muted">Loading AI Resources...</p>
                      <Button variant="contained" className="mt-4" onClick={() => openCurrentTask("resources")}>Generate Resources</Button>
                    </div>
                  </div>
                )}

                {modalTab === "video" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 text-center py-12">
                    <PlayCircleIcon className="h-12 w-12 text-primary/20 mx-auto mb-4" />
                    <p className="text-muted">Video Tutorial will appear here.</p>
                  </div>
                )}

                {modalTab === "submit" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
                    <div className={`p-6 rounded-2xl border-2 border-dashed ${isDark ? "border-accentGray/20" : "border-gray-200"}`}>
                      <h4 className="font-bold mb-4 flex items-center gap-2">
                        <GitHubIcon className="h-5 w-5" /> GitHub Repository
                      </h4>
                      {(githubRepoUrl || githubStatus?.repo_url) ? (
                        <div className="space-y-4">
                          <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/10" : "bg-gray-50"} flex items-center justify-between gap-3`}>
                            <code className="text-sm text-primary truncate flex-1">{githubRepoUrl || githubStatus?.repo_url}</code>
                            <div className="flex gap-2">
                              <button 
                                onClick={syncInvitationStatus}
                                className="p-2 rounded-lg hover:bg-primary/10 transition-colors text-primary"
                                title="Refresh status"
                              >
                                <ArrowPathIcon className="h-4 w-4" />
                              </button>
                              <a 
                                href={githubRepoUrl || githubStatus?.repo_url} 
                                target="_blank" 
                                rel="noreferrer" 
                                className="px-3 py-1 bg-primary text-white text-xs font-bold rounded-lg hover:bg-secondary transition-colors flex items-center gap-1"
                              >
                                <ArrowTopRightOnSquareIcon className="h-3 w-3" /> Open
                              </a>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <div className={`h-2 w-2 rounded-full ${(invitationStatus === 'active' || invitationStatus === 'ACCEPTED' || invitationStatus === 'READY') ? 'bg-green-500' : 'bg-yellow-500'}`} />
                            <span className="text-muted">Invitation Status: <span className="font-bold capitalize">{invitationStatus || 'pending'}</span></span>
                          </div>
                          {(invitationStatus !== 'active' && invitationStatus !== 'ACCEPTED' && invitationStatus !== 'READY') && (
                            <div className="space-y-3">
                              <p className="text-xs text-muted">Please check your email and accept the GitHub invitation to enable AI reviews.</p>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                onClick={syncInvitationStatus}
                                startIcon={<ArrowPathIcon className="h-4 w-4" />}
                              >
                                Check Invitation Status
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-muted italic">No repository provisioned. Please contact support if you enrolled with a GitHub username.</p>
                            <button 
                              onClick={syncInvitationStatus}
                              className="text-xs text-primary font-bold hover:underline flex items-center gap-1"
                            >
                              <ArrowPathIcon className="h-3 w-3" /> Check Status
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-end">
                      <Button 
                        variant="contained" 
                        disabled={!(invitationStatus === 'active' || invitationStatus === 'ACCEPTED' || invitationStatus === 'READY')}
                        onClick={() => openCurrentTask("submit")}
                      >
                        Proceed to Review
                      </Button>
                    </div>
                  </div>
                )}
                
                {modalTab === "feedback" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 text-center py-12">
                    <ChatBubbleLeftRightIcon className="h-12 w-12 text-primary/20 mx-auto mb-4" />
                    <p className="text-muted">No feedback available yet. Submit your task first.</p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="mt-4 text-muted font-medium">Preparing your workspace...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

 </main>

 {showModal && viewingTask && (
 <TaskModal
 key={`${viewIndex}-${viewingTask.id}`}
 task={viewingTask}
 onClose={() => setShowModal(false)}
 tab={modalTab}
 setTab={setModalTab}
 reviewState={reviewStateById[viewingTask.id]}
        onSubmit={async ({ docFile, imgFile, comments, githubUrl, submissionType, setSubmitting, setServerError }) => {
          try {
            setServerError(""); setSubmitting(true);
            const pendingMap = { ...reviewStateById, [viewingTask.id]: { status: "pending" } };
            writeProgress(userId, bunchName, { completedTaskIds, reviewStateById: pendingMap });
            setReviewStateById(pendingMap);
            // Use provisioned GitHub URL if available
            const finalGithubUrl = githubRepoUrl || githubUrl;
            const finalSubmissionType = githubRepoUrl ? "github" : submissionType;

            const result = await reviewTaskWithAI({ 
              userId, bunchName, experienceLevel, task: viewingTask, 
              docFile, imgFile, comments, 
              githubUrl: finalGithubUrl, 
              submissionType: finalSubmissionType 
            });
 const nextReview = { status: result.passed ?"passed" :"failed", feedback: result.feedback ||"", score: result.score ?? null };
 const nextReviewState = { ...pendingMap, [viewingTask.id]: nextReview };
 let nextCompleted = completedTaskIds;
 if (result.passed) {
 nextCompleted = Array.from(new Set([...completedTaskIds, viewingTask.id]));
 // Mark task as completed in calendar when task is passed
 markTaskCompleted();
 }
 setCompletedTaskIds(nextCompleted);
 setReviewStateById(nextReviewState);
 writeProgress(userId, bunchName, { completedTaskIds: nextCompleted, reviewStateById: nextReviewState });
 
 // Sync progress to backend for certificate tracking
 syncProgressToBackend(userId, bunchName, nextCompleted, tasks.length);
 
 setModalTab("feedback");
 } catch (e) {
 setServerError(String(e?.message || e));
 } finally { setSubmitting(false); }
 }}
 visualize={{
 show: true,
 visualizing,
 visualUrl,
 visualHint,
 visualGeneratedAt,
 setVisualHint,
 onVisualize: handleVisualize
 }}
 explanation={{
 loading: explanationLoading, explanation: topicExplanation, onGenerate: generateTopicExplanation, language: explanationLanguage
 }}
        explanationLanguage={explanationLanguage}
        setExplanationLanguage={() => {}} // Language selection removed - always English
        githubRepoUrl={githubRepoUrl}
        invitationStatus={invitationStatus}
        githubStatus={githubStatus}
        syncInvitationStatus={syncInvitationStatus}
        explanationUsage={explanationUsage}
        videoUsage={videoUsage}
        video={{
 generating: videoGenerating,
 url: videoUrl,
 status: videoStatus,
 message: videoMessage,
 error: videoError,
 onGenerate: generateVideo,
 onLoaded: handleVideoLoaded,
 onError: handleVideoLoadError
 }}
 />
 )}

 {/* 🎯 LLM Trial Active Modal */}
 {showTrialModal && llmTrialStatus?.hasActiveTrial && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-bgMain/70 p-4">
 <div className="w-full max-w-md bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 border border-blue-400/50 rounded-2xl shadow-2xl p-8 text-center">
 <div className="text-5xl mb-4">🎉</div>
 <h2 className="text-2xl font-bold text-blue-100 mb-4">Welcome to AI Mentor!</h2>
 <p className="text-blue-200 mb-6 text-sm leading-relaxed">
 You have a <strong className="text-primary">7-day FREE trial</strong> to access our premium AI Mentor feature. Get personalized guidance, ask questions, and accelerate your learning!
 </p>
 <div className="bg-blue-800/50 border border-blue-400/30 rounded-lg p-4 mb-6">
 <p className="text-blue-100 font-semibold">
 Trial expires in <span className="text-primary text-lg">{llmTrialStatus?.daysRemaining}</span> days
 </p>
 </div>
 <button
 onClick={() => {
 setShowTrialModal(false);
 setShowMentor(true);
 }}
 className="w-full bg-primary text-white font-bold py-3 rounded-lg hover:bg-secondary transition mb-3"
 >
 Start Your Free Trial →
 </button>
 <button
 onClick={() => setShowTrialModal(false)}
 className="w-full bg-blue-700/50 border border-blue-400/50 text-blue-100 font-medium py-2 rounded-lg hover:bg-blue-700 transition"
 >
 Maybe Later
 </button>
 </div>
 </div>
 )}

 {/* 🔒 LLM Trial Expired Modal */}
 {showTrialExpiredModal && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-bgMain/70 p-4">
 <div className="w-full max-w-md bg-gradient-to-br from-red-900 via-orange-900 to-red-900 border border-red-400/50 rounded-2xl shadow-2xl p-8 text-center">
 <div className="text-5xl mb-4">⏰</div>
 <h2 className="text-2xl font-bold text-red-100 mb-4">Trial Period Expired</h2>
 <p className="text-red-200 mb-6 text-sm leading-relaxed">
 Your <strong className="text-primary">7-day free trial</strong> for AI Mentor has ended. Upgrade to <strong className="text-primary">Premium</strong> to continue accessing advanced features!
 </p>
 <div className="bg-red-800/50 border border-red-400/30 rounded-lg p-4 mb-6">
 <ul className="text-red-100 text-sm space-y-2 text-left">
 <li>✨ Unlimited AI Mentor Access</li>
 <li>🎯 Advanced Challenge Levels</li>
 <li>📊 Personalized Learning Paths</li>
 <li>🏆 Certificate Programs</li>
 </ul>
 </div>
 <button
 onClick={() => {
 setShowTrialExpiredModal(false);
 navigate('/subscription-plans');
 }}
 className="w-full bg-primary text-white font-bold py-3 rounded-lg hover:bg-secondary transition mb-3"
 >
 Upgrade to Premium Now
 </button>
 <button
 onClick={() => setShowTrialExpiredModal(false)}
 className="w-full bg-red-700/50 border border-red-400/50 text-red-100 font-medium py-2 rounded-lg hover:bg-red-700 transition"
 >
 Close
 </button>
 </div>
 </div>
 )}

 {showMentor && (
 <MentorModal
 onClose={() => setShowMentor(false)}
 messages={mentorMessages}
 setMessages={(m) => { setMentorMessages(m); writeChat(userId, bunchName, m); }}
 onSend={async ({ text, imgFile }) => sendMentorMessage({ text, imgFile })}
 technology={bunchName}
 task={tasks[unlockedIndex]}
 />
 )}

 {/* Voice Assistant Modal */}
 {isVoiceAssistantOpen && (
   <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
     <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl bg-gradient-to-br from-indigo-900 via-purple-900 to-black border border-white/20">
       <button 
         onClick={closeVoiceAssistant}
         className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
       >
         <XMarkIcon className="h-6 w-6" />
       </button>
       <div className="p-6">
         <VoiceAssistant
           isOpen={isVoiceAssistantOpen}
           onClose={closeVoiceAssistant}
           initialMessage={vaInitialMessage}
           onCommand={onVoiceCommand}
           onNavigate={onVoiceNavigate}
           currentTask={viewingTask}
           userContext={{
             userId,
             roleName: bunchName,
             experienceLevel,
             currentTaskIndex: viewIndex,
             totalTasks: tasks.length
           }}
         />
       </div>
     </div>
   </div>
 )}
 <AchievementSystem userId={userId} userProgress={enrollment?.progress} dashboardType="individualroles" />
 </div>
 );
}

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-sky-200 bg-[#f0f9ff] p-5 shadow-sm text-black ${className}`}>
    {children}
  </div>
);

function Pill({ children, tone = "yellow" }) {
  const map = {
    yellow: "text-black border-black/20 bg-white",
    green: "text-green-700 border-green-500/40 bg-green-500/10",
    red: "text-red-700 border-red-500/40 bg-red-500/10",
    amber: "text-amber-700 border-accent/40 bg-accent/10",
    slate: "text-slate-600 border-zinc-300 bg-white",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${map[tone]}`}>{children}</span>
  );
}

function ActionCard({ title, subtitle, icon, onClick }) {
 return (
 <button
 onClick={onClick}
 className="flex flex-col items-start gap-3 rounded-xl border border-secondary/30 bg-bgMain/40 p-5 text-left hover:border-primary hover:bg-secondary/5 transition-all duration-200 group"
 >
 <div className="flex items-center gap-3">
 <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition">
 {icon}
 </div>
 <h4 className="font-semibold text-sm group-hover:text-primary transition">{title}</h4>
 </div>
 <p className="text-xs text-muted group-hover:text-muted transition leading-relaxed">
 {subtitle.length > 50 ? `${subtitle.substring(0, 50)}...` : subtitle}
 </p>
 </button>
 );
}

function LinearProgress({ percent }) {
 return (
 <div className="mt-3 w-full bg-bgSection rounded-full h-2">
 <div
 className="bg-primary h-2 rounded-full transition-all duration-300"
 style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
 />
 </div>
 );
}

function ProgressRing({ percent }) {
 const radius = 80;
 const strokeWidth = 12;
 const normalizedRadius = radius - strokeWidth * 2;
 const circumference = normalizedRadius * 2 * Math.PI;
 const strokeDasharray = `${circumference} ${circumference}`;
 const strokeDashoffset = circumference - (percent / 100) * circumference;

 return (
 <div className="flex items-center justify-center">
 <svg height={radius * 2} width={radius * 2} className="transform -rotate-90">
 <circle
 stroke="rgb(55 65 81)"
 fill="transparent"
 strokeWidth={strokeWidth}
 r={normalizedRadius}
 cx={radius}
 cy={radius}
 />
 <circle
 stroke="rgb(253 224 71)"
 fill="transparent"
 strokeWidth={strokeWidth}
 strokeDasharray={strokeDasharray}
 style={{ strokeDashoffset }}
 r={normalizedRadius}
 cx={radius}
 cy={radius}
 className="transition-all duration-300"
 />
 </svg>
 <div className="absolute text-2xl font-extrabold text-primary">
 {percent}%
 </div>
 </div>
 );
}

function QuickLink({ label, icon, onClick }) {
 return (
 <button
 onClick={onClick}
 className="rounded-xl border border-secondary/50 text-primary hover:text-black hover:bg-primary hover:border-primary transition px-4 py-2 text-sm flex items-center gap-2"
 >
 {icon} {label}
 </button>
 );
}

function Screen({ children }) {
 return (
 <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center transition-colors duration-300">
 {children}
 </div>
 );
}

function Denied({ text }) {
 return (
 <div className="max-w-md w-full rounded-xl border border-secondary/40 bg-[#0b0b0b] p-5">
 <div className="text-primary font-semibold mb-2">Access restricted</div>
 <div className="text-sm text-muted whitespace-pre-line">{text}</div>
 </div>
 );
}

/* ------------------------------- Task Modal -------------------------------- */
function TaskModal({ task, onClose, tab, setTab, reviewState, onSubmit, visualize, explanation, video, explanationLanguage, setExplanationLanguage, githubRepoUrl, invitationStatus, explanationUsage, videoUsage, explainWithAI }) {
  const [docFile, setDocFile] = useState(null);
  const [imgFile, setImgFile] = useState(null);
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [submissionType, setSubmissionType] = useState(githubRepoUrl ? "github" : "files");
  const [githubUrl, setGithubUrl] = useState(githubRepoUrl || "");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-4xl bg-[#f0f9ff] border border-sky-200 rounded-2xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-sky-100">
          <div className="flex items-center gap-2">
            <ClipboardDocumentListIcon className="h-5 w-5 text-black" />
            <h3 className="font-bold text-black">Task</h3>
          </div>
 <button className="p-1 rounded hover:bg-zinc-800" onClick={onClose}><XMarkIcon className="h-5 w-5 text-muted" /></button>
 </div>

 <div className="px-5 pt-3">
 <div className="flex gap-2 flex-wrap">
              <Tab active={tab==="overview"} onClick={()=>setTab("overview")}>Overview</Tab>
              <Tab active={tab==="resources"} onClick={()=>setTab("resources")}>Resources</Tab>
              <Tab active={tab==="video"} onClick={()=>setTab("video")}>Video</Tab>
              <Tab active={tab==="submit"} onClick={()=>setTab("submit")}>Submit</Tab>
              <Tab active={tab==="feedback"} onClick={()=>setTab("feedback")}>Feedback</Tab>
            </div>
 </div>

 <div className="px-5 py-4 max-h-[70vh] overflow-y-auto space-y-4">
 {tab ==="overview" && (
 <div className="space-y-4">
 <Field label="Topic"><div className="text-base font-semibold">{task.title || task.topic}</div></Field>
 {task.description && <Field label="Description"><p className="text-sm text-gray-200 whitespace-pre-line">{task.description}</p></Field>}
 {task.useCase && <Info label="Use case"><DocumentTextIcon className="h-4 w-4 text-primary" /> {task.useCase}</Info>}
 {task.futureUseCase && <Info label="Future use case"><ShieldCheckIcon className="h-4 w-4 text-primary" /> {task.futureUseCase}</Info>}
 {task.expectedOutput && <Info label="Expected output"><CheckCircleIcon className="h-4 w-4 text-primary" /> {task.expectedOutput}</Info>}
 {task.externalTools?.length ? <Info label="External tools"><span>{task.externalTools.join(",")}</span></Info> : null}
 {task.subtopics?.length > 0 && (<div><div className="text-sm text-muted mb-2">Subtopics ({task.subtopics.length})</div>
 <ul className="list-disc pl-5 space-y-1 text-sm text-gray-200">{task.subtopics.map((s,i)=><li key={i}>{s}</li>)}</ul></div>)}
 </div>
 )}

 {tab ==="resources" && (
 <div className="space-y-6">
 {/* AI Generated Explanation */}
 {explanation?.explanation && (
 <div className="space-y-4">

 {/* Topic Overview */}
 {explanation.explanation.topicOverview && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <BookOpenIcon className="h-5 w-5" />
 Topic Overview
 </h4>
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.topicOverview)}
 </p>
 </div>
 )}

 {/* Key Concepts */}
 {explanation.explanation.keyConcepts && explanation.explanation.keyConcepts.length > 0 && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <ClipboardDocumentListIcon className="h-5 w-5" />
 Key Concepts
 </h4>
 </div>
 <ul className="space-y-2">
 {explanation.explanation.keyConcepts.map((concept, index) => (
 <li key={index} className="flex items-start gap-2 text-sm text-muted">
 <span className="text-primary mt-1">•</span>
 <span>{safeToString(concept)}</span>
 </li>
 ))}
 </ul>
 </div>
 )}

 {/* Subtopic Explanations */}
 {explanation.explanation.subtopicExplanations && Object.keys(explanation.explanation.subtopicExplanations).length > 0 && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <ClipboardDocumentListIcon className="h-5 w-5" />
 Detailed Subtopic Explanations
 </h4>
 </div>
 <div className="space-y-4">
 {Object.entries(explanation.explanation.subtopicExplanations).map(([subtopic, explanationText], index) => (
 <div key={index} className="rounded-lg border border-secondary/10 bg-bgMain/20 p-4">
 <div className="flex items-center justify-between mb-2">
 <h5 className="text-sm font-semibold text-gray-100">{safeToString(subtopic)}</h5>
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">{safeToString(explanationText)}</p>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Practical Examples */}
 {explanation.explanation.practicalExamples && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <span>💡</span>
 Practical Examples
 </h4>
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.practicalExamples)}
 </p>
 </div>
 )}

 {/* Common Mistakes */}
 {explanation.explanation.commonMistakes && (
 <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-red-400 flex items-center gap-2">
 <ExclamationTriangleIcon className="h-5 w-5" />
 Common Mistakes to Avoid
 </h4>
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.commonMistakes)}
 </p>
 </div>
 )}

 {/* Best Practices */}
 {explanation.explanation.bestPractices && (
 <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-green-400 flex items-center gap-2">
 <CheckCircleIcon className="h-5 w-5" />
 Best Practices
 </h4>
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.bestPractices)}
 </p>
 </div>
 )}
 </div>
 )}

 {/* Loading State */}
 {explanation?.loading && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-8">
 <div className="text-center">
 <ArrowPathIcon className="h-8 w-8 text-primary animate-spin mx-auto mb-4" />
 <h4 className="text-lg font-semibold text-primary mb-2">Generating AI Explanation</h4>
 <p className="text-sm text-muted">
 Creating a comprehensive explanation for <strong>{task.topic}</strong>...
 </p>
 </div>
 </div>
 )}

 {/* Fallback UI */}
 {!explanation?.explanation && !explanation?.loading && (
 <div className="space-y-4">
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="text-center py-8">
 <BookOpenIcon className="h-12 w-12 text-primary mx-auto mb-4" />
 <h4 className="text-lg font-semibold text-primary mb-2">Ready to Learn?</h4>
 <p className="text-sm text-muted mb-4">
 Generate a comprehensive AI-powered explanation of <strong>{task.topic}</strong>
 tailored specifically for your experience level and course.
 </p>
 <button
 onClick={explanation?.onGenerate}
 className="rounded-xl bg-primary text-white px-6 py-3 text-sm font-semibold hover:bg-primary flex items-center gap-2 mx-auto"
 >
 <BookOpenIcon className="h-5 w-5" />
 Generate Topic Explanation {explanationUsage ? ` (${explanationUsage.explanations_remaining} left)` : ''}
 </button>
 </div>
 </div>

 {/* Basic topic information as fallback */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
 <span>📖</span>
 Basic Topic Information
 </h4>
 <div className="space-y-3">
 {task.description && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Description</h5>
 <p className="text-sm text-muted leading-relaxed">{task.description}</p>
 </div>
 )}

 {task.useCase && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Use Case</h5>
 <p className="text-sm text-muted leading-relaxed">{task.useCase}</p>
 </div>
 )}

 {task.expectedOutput && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Expected Output</h5>
 <p className="text-sm text-muted leading-relaxed">{task.expectedOutput}</p>
 </div>
 )}

 {task.subtopics && task.subtopics.length > 0 && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Subtopics ({task.subtopics.length})</h5>
 <ul className="list-disc pl-5 space-y-1 text-sm text-muted">
 {task.subtopics.slice(0, 10).map((subtopic, index) => (
 <li key={index}>{subtopic}</li>
 ))}
 {task.subtopics.length > 10 && (
 <li className="text-primary">... and {task.subtopics.length - 10} more subtopics</li>
 )}
 </ul>
 </div>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 )}

 {tab ==="video" && (
 <div className="space-y-6">
 {/* Video Generation Section */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-6">
 <div className="flex items-center gap-3 mb-4">
 <VideoCameraIcon className="h-6 w-6 text-primary" />
 <h4 className="text-lg font-semibold text-primary">🎬 AI Tutorial Video Generator</h4>
 </div>
 
 <div className="space-y-4">
 {/* Video Info Card */}
 <div className="bg-primary/10 border border-secondary/30 rounded-lg p-4">
 <div className="flex items-start gap-3">
 <div className="bg-secondary/20 p-2 rounded-lg">
 <VideoCameraIcon className="h-5 w-5 text-primary" />
 </div>
 <div className="flex-1">
 <h5 className="font-semibold text-primary mb-1">What You'll Get</h5>
 <p className="text-sm text-muted mb-2">
 A comprehensive tutorial video for <strong className="text-primary">{task.topic}</strong>
 </p>
 <div className="grid grid-cols-2 gap-2 text-xs text-muted">
 <div className="flex items-center gap-1">
 <span className="w-2 h-2 bg-primary rounded-full"></span>
 Duration: 60-90 seconds
 </div>
 <div className="flex items-center gap-1">
 <span className="w-2 h-2 bg-primary rounded-full"></span>
 HD Quality (1280x720)
 </div>
 <div className="flex items-center gap-1">
 <span className="w-2 h-2 bg-primary rounded-full"></span>
 {task.subtopics?.length || 0} subtopics covered
 </div>
 <div className="flex items-center gap-1">
 <span className="w-2 h-2 bg-primary rounded-full"></span>
 Code examples included
 </div>
 </div>
 </div>
 </div>
 </div>
 
 {!video?.url && video?.status !=="queued" ? (
 <div className="space-y-4">
 {/* Enhanced Generate Button */}
 <button
 onClick={video?.onGenerate}
 disabled={video?.generating}
 className="w-full bg-primary hover:bg-secondary disabled:bg-primary/50 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl disabled:shadow-none"
 >
 {video?.generating ? (
 <>
 <div className="animate-spin rounded-full h-6 w-6 border-2 border-black border-t-transparent"></div>
 <span>Generating Your Tutorial...</span>
 </>
 ) : (
 <>
 <VideoCameraIcon className="h-6 w-6" />
 <span>Generate Tutorial Video {videoUsage ? ` (${videoUsage.video_remaining || videoUsage.videos_remaining} left)` : ''}</span>
 </>
 )}
 </button>
 
 {/* Progress Info */}
 {video?.generating && (
 <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
 <div className="flex items-center gap-3 mb-3">
 <div className="animate-pulse bg-blue-500 w-3 h-3 rounded-full"></div>
 <span className="font-semibold text-blue-400">Creating Your Tutorial Video</span>
 </div>
 <div className="space-y-2 text-sm text-muted">
 <div className="flex justify-between">
 <span>Topic:</span>
 <span className="text-primary font-medium">{task.topic}</span>
 </div>
 <div className="flex justify-between">
 <span>Estimated Time:</span>
 <span className="text-green-400">1-2 minutes</span>
 </div>
 <div className="flex justify-between">
 <span>Content:</span>
 <span className="text-blue-400">{task.subtopics?.length || 0} subtopics + examples</span>
 </div>
 </div>
 <div className="mt-3 text-xs text-muted">
 💡 Please don't close this window while generating...
 </div>
 </div>
 )}
 
 {/* Features List */}
 {!video?.generating && (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Professional narration</span>
 </div>
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Visual slides & code</span>
 </div>
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Real-world examples</span>
 </div>
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Best practices</span>
 </div>
 </div>
 )}
 </div>
 ) : (
 <div className="space-y-4">
 {/* Success Message */}
 <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4">
 <div className="flex items-center gap-3 mb-2">
 <div className="bg-green-500/20 p-2 rounded-lg">
 <CheckCircleIcon className="h-5 w-5 text-green-400" />
 </div>
 <div>
 <h5 className="font-semibold text-green-400">
 {video?.status ==="ready" ?"🎉 Video Generated Successfully!" :"⏳ Video rendering in progress"}
 </h5>
 <p className="text-sm text-muted">
 {video?.status ==="ready"
 ?"Your tutorial video is ready to watch"
 : video?.message ||"We're refreshing the player automatically until the MP4 is ready."}
 </p>
 </div>
 </div>
 </div>
 
 {/* Video Player */}
 <div className="space-y-3">
 <div className="relative bg-bgMain rounded-lg overflow-hidden shadow-lg">
 {video?.url ? (
 <>
 <video
 controls
 className="w-full rounded-lg"
 style={{ maxHeight: '400px' }}
 poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4MCIgaGVpZ2h0PSI3MjAiIHZpZXdCb3g9IjAgMCAxMjgwIDcyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEyODAiIGhlaWdodD0iNzIwIiBmaWxsPSIjMUYyOTM3Ii8+CjxjaXJjbGUgY3g9IjY0MCIgY3k9IjM2MCIgcj0iNDAiIGZpbGw9IiNGQkJGMjQiLz4KPHBhdGggZD0iTTYyMCAzNDBMMjYwIDM0MEwyNjAgMzgwTDYyMCAzODBMNjIwIDM0MFoiIGZpbGw9IiMxRjI5MzciLz4KPC9zdmc+"
 onCanPlay={video?.onLoaded}
 onLoadedData={video?.onLoaded}
 onError={video?.onError}
 >
 <source src={getProxiedVideoUrl(video.url)} type="video/mp4" />
 Your browser does not support the video tag.
 </video>
 {video?.status !=="ready" && (
 <div className="absolute inset-0 bg-bgMain/70 flex flex-col items-center justify-center text-center p-4">
 <ArrowPathIcon className="h-8 w-8 text-primary animate-spin mb-3" />
 <p className="text-sm text-gray-200">
 {video?.message ||"Waiting for the video to finish rendering…"}
 </p>
 </div>
 )}
 </>
 ) : (
 <div className="p-6 text-center">
 {video?.status ==="queued" || video?.status ==="generating" ? (
 <div className="flex flex-col items-center justify-center gap-3">
 <ArrowPathIcon className="h-8 w-8 text-primary animate-spin" />
 <p className="text-sm text-gray-200 font-medium">
 {video?.message ||"Rendering video… This usually takes 1-3 minutes."}
 </p>
 <p className="text-xs text-muted">
 We'll update automatically once the video is ready.
 </p>
 </div>
 ) : (
 <div className="text-sm text-muted">
 Video URL not available yet. Generate a video to get started.
 </div>
 )}
 </div>
 )}
 </div>
 {video?.error && (
 <div className="bg-red-500/10 border border-red-500/30 text-red-200 text-sm rounded-lg p-3">
 {video.error}
 </div>
 )}
 
 {/* Action Buttons */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <button
 onClick={() => window.open(video?.url, '_blank')}
 disabled={video?.status !=="ready"}
 className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
 title={video?.status ==="ready" ?"Open video in a new tab" :"Video still rendering"}
 >
 <ArrowTopRightOnSquareIcon className="h-5 w-5" />
 Open in New Tab
 </button>
 <button
 onClick={async () => {
 try {
 const response = await fetch(video?.url);
 const blob = await response.blob();
 const url = window.URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = url;
 link.download = `tutorial-${task.topic.replace(/[^a-zA-Z0-9]/g, '-')}.mp4`;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 window.URL.revokeObjectURL(url);
 } catch (err) {
 console.error('Download failed:', err);
 alert('Download failed. Please try opening in a new tab instead.');
 }
 }}
 disabled={video?.status !=="ready"}
 className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
 title={video?.status ==="ready" ?"Download MP4" :"Video still rendering"}
 >
 <ArrowDownTrayIcon className="h-5 w-5" />
 Download Video
 </button>
 </div>
 
 {/* Regenerate Button */}
 <button
 onClick={video?.onGenerate}
 className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
 >
 <ArrowPathIcon className="h-4 w-4" />
 Regenerate Video
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 )}

    {tab === "submit" && (
      <div className="space-y-6">
        {/* Provisioned Repo Notice */}
        {githubRepoUrl && (
          <div className="rounded-xl border border-primary/40 bg-primary/10 p-4 mb-4">
            <h4 className="text-sm font-semibold text-primary mb-1 flex items-center gap-2">
              <CheckCircleIcon className="h-4 w-4" />
              Your Dedicated Project Repository
            </h4>
            <p className="text-xs text-muted mb-2">
              We've created a private GitHub repository for your project. Please push your code here.
            </p>
            <div className="flex items-center gap-3">
              <code className="bg-bgMain/60 px-2 py-1 rounded text-xs text-primary border border-primary/20 flex-1 truncate">
                {githubRepoUrl}
              </code>
              <a 
                href={githubRepoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs bg-primary text-white px-3 py-1 rounded hover:bg-secondary transition"
              >
                Open Repo
              </a>
            </div>
            {invitationStatus === 'SENT' && (
              <p className="text-[10px] text-orange-400 mt-2 italic">
                ⚠️ Check your email to accept the GitHub invitation if you haven't yet.
              </p>
            )}
          </div>
        )}

        {/* Submission Type Selection */}
        {!githubRepoUrl && (
          <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
            <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5" />
              Choose Submission Type
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setSubmissionType("github")}
                className={`p-4 rounded-lg border transition-all ${
                  submissionType === "github" 
                  ? "border-primary bg-primary/10" 
                  : "border-secondary/30 hover:border-primary/50"
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">🐙</div>
                  <h5 className="font-semibold text-primary mb-1">GitHub Repository</h5>
                  <p className="text-xs text-muted">Submit your code repository</p>
                </div>
              </button>
              <button
                onClick={() => setSubmissionType("files")}
                className={`p-4 rounded-lg border transition-all ${
                  submissionType === "files" 
                  ? "border-primary bg-primary/10" 
                  : "border-secondary/30 hover:border-primary/50"
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">📄</div>
                  <h5 className="font-semibold text-primary mb-1">Document + Image</h5>
                  <p className="text-xs text-muted">Upload documentation and screenshots</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* File Upload Section */}
        {!githubRepoUrl && submissionType === "files" && (
          <div className="space-y-4">
            <p className="text-sm text-muted">Upload your <b className="text-primary">document</b> and an <b className="text-primary">image</b>.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Uploader label="Document" accept=".pdf,.doc,.docx,.md,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/markdown" file={docFile} setFile={setDocFile}/>
              <Uploader label="Image" accept="image/*" file={imgFile} setFile={setImgFile}/>
            </div>
          </div>
        )}

        {/* GitHub URL Section */}
        {submissionType === "github" && (
          <div className="space-y-4">
            <p className="text-sm text-muted">
              {githubRepoUrl 
                ? "We'll review the code in your dedicated repository." 
                : "Provide your GitHub repository URL for code review."
              }
            </p>
            {!githubRepoUrl && (
              <label className="block">
                <span className="text-xs text-muted">GitHub Repository URL</span>
                <input 
                  type="url" 
                  className="mt-1 w-full rounded-lg bg-bgMain/40 border border-zinc-700 text-sm text-gray-200 p-3 focus:outline-none focus:border-secondary" 
                  placeholder="https://github.com/username/repository" 
                  value={githubUrl} 
                  onChange={(e)=>setGithubUrl(e.target.value)} 
                  disabled={submitting}
                />
              </label>
            )}
          </div>
        )}

 {/* Comments Section */}
 <label className="block">
 <span className="text-xs text-muted">Notes for reviewer (optional)</span>
 <textarea 
 className="mt-1 w-full rounded-lg bg-bgMain/40 border border-zinc-700 text-sm text-gray-200 p-3 focus:outline-none focus:border-secondary" 
 rows={3} 
 placeholder="Anything you'd like to highlight…" 
 value={comments} 
 onChange={(e)=>setComments(e.target.value)} 
 disabled={submitting}
 />
 </label>

 {serverError && <div className="text-xs text-red-400 flex items-center gap-2"><ExclamationTriangleIcon className="h-4 w-4"/> {serverError}</div>}
 
 <div className="flex justify-end">
 <button
 onClick={()=>onSubmit({ docFile, imgFile, comments, githubUrl, submissionType, setSubmitting, setServerError })}
 disabled={submitting || (submissionType ==="files" && (!docFile || !imgFile)) || (submissionType ==="github" && !githubUrl)}
 className="rounded-xl bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary disabled:opacity-60 flex items-center gap-2"
 >
 {submitting ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckCircleIcon className="h-4 w-4" />}
 {submitting ?"Submitting…" :"Submit for AI review"}
 </button>
 </div>
 </div>
 )}

 {tab ==="feedback" && (
 !reviewState ? <p className="text-sm text-muted">No submission yet. Use the"Submit" tab.</p> :
 reviewState.status ==="pending" ? <p className="text-sm text-primary flex items-center gap-2"><ArrowPathIcon className="h-4 w-4 animate-spin"/> Submission received — awaiting AI review…</p> :
 reviewState.status ==="passed" ? (
 <div className="space-y-3">
 <div className="rounded-xl border border-green-500/40 bg-green-500/10 p-4">
 <div className="font-semibold text-green-400 flex items-center gap-2"><CheckCircleIcon className="h-5 w-5" />Passed</div>
 {"score" in reviewState && reviewState.score != null && <div className="text-xs text-muted mt-1">Score: {(reviewState.score*100).toFixed(0)}%</div>}
 {reviewState.feedback && <p className="text-sm text-gray-200 mt-2 whitespace-pre-line">{reviewState.feedback}</p>}
 </div>
 </div>
 ) : (
 <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4">
 <div className="font-semibold text-red-400 flex items-center gap-2"><ExclamationTriangleIcon className="h-5 w-5" />Changes needed</div>
 {reviewState.feedback && <p className="text-sm text-gray-200 mt-2 whitespace-pre-line">{reviewState.feedback}</p>}
 <div className="text-xs text-muted mt-2">Update your document/image and resubmit from the"Submit" tab.</div>
 </div>
 )
 )}

 {tab ==="visualize" && visualize?.show && (
 <div className="space-y-6">
 {/* Header Section */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
 <PhotoIcon className="h-5 w-5" />
 AI-Powered Visualization Generator
 </h4>
 <p className="text-sm text-muted leading-relaxed">
 Generate <b className="text-primary">task-specific visualizations</b> including architecture diagrams, 
 dashboards, flowcharts, and mockups to help you understand how real-world implementations look.
 </p>
 </div>

 {/* Visualization Options */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <h5 className="text-sm font-semibold text-gray-200 mb-3">Visualization Types</h5>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
 {[
 { type:"architecture", label:"Architecture Diagram", icon:"🏗️" },
 { type:"dashboard", label:"Dashboard Mockup", icon:"📊" },
 { type:"flowchart", label:"Process Flow", icon:"🔄" },
 { type:"ui", label:"UI/UX Design", icon:"🎨" }
 ].map((option) => (
 <button
 key={option.type}
 className="p-3 rounded-lg border border-secondary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/40 transition text-center"
 onClick={() => visualize.setVisualHint(`${option.label}: ${visualize.visualHint}`)}
 >
 <div className="text-2xl mb-1">{option.icon}</div>
 <div className="text-xs text-gray-200">{option.label}</div>
 </button>
 ))}
 </div>
 </div>

 {/* Task Context Section */}
 <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
 <h5 className="text-sm font-semibold text-blue-400 mb-3 flex items-center gap-2">
 <span>📋</span>
 Task Context (Auto-filled for better results)
 </h5>
 <div className="space-y-2 text-xs text-muted">
 <div><span className="text-blue-400">Project:</span> {task?.projectName ||"N/A"}</div>
 <div><span className="text-blue-400">Use Case:</span> {task?.useCase ||"N/A"}</div>
 <div><span className="text-blue-400">Expected Output:</span> {task?.expectedOutput ||"N/A"}</div>
 </div>
 </div>

 {/* Input Section */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <label className="block">
 <span className="text-sm font-medium text-gray-200 mb-2 block">
 Custom Visualization Prompt
 </span>
 <textarea
 rows={4}
 className="w-full rounded-lg bg-bgMain/40 border border-zinc-700 text-sm text-gray-200 p-3 focus:outline-none focus:border-secondary resize-none"
 value={visualize.visualHint}
 onChange={(e)=>visualize.setVisualHint(e.target.value)}
 placeholder={`Based on: ${task?.projectName ||"this project"} - ${task?.useCase ||"the use case"}

Describe what you want to visualize... e.g., 'System architecture diagram showing the components and data flow' or 'User interface mockup with the main features'`}
 />
 </label>
 
 <div className="mt-3 flex items-center justify-between">
 <div className="text-xs text-muted">
 💡 Be specific about components, layout, and style for better results
 </div>
 <button
 onClick={visualize.onVisualize}
 disabled={visualize.visualizing}
 className="rounded-xl bg-primary text-white px-6 py-3 text-sm font-semibold hover:bg-primary disabled:opacity-60 flex items-center gap-2"
 >
 {visualize.visualizing ? (
 <>
 <ArrowPathIcon className="h-4 w-4 animate-spin" />
 Generating...
 </>
 ) : (
 <>
 <PhotoIcon className="h-4 w-4" />
 Generate Visualization
 </>
 )}
 </button>
 </div>
 </div>

 <VisualizationPreview
 loading={visualize.visualizing}
 imageUrl={visualize.visualUrl}
 taskTitle={task?.title || task?.topic}
 hint={visualize.visualHint}
 generatedAt={visualize.visualGeneratedAt}
 downloadFileName={`${slugify(task?.title || task?.topic || 'visual')}-visual.png`}
 />

 {/* Tips Section */}
 <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
 <h5 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-2">
 <span>💡</span>
 Pro Tips for Better Visualizations
 </h5>
 <ul className="text-xs text-muted space-y-1 list-disc pl-4">
 <li>Be specific about technologies, frameworks, and components</li>
 <li>Include layout preferences (e.g.,"side-by-side","top-down")</li>
 <li>Mention color schemes or design styles if important</li>
 <li>Specify the level of detail (high-level overview vs detailed components)</li>
 <li>Include any specific tools or platforms (AWS, React, etc.)</li>
 </ul>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 );
}

const Tab = ({ active, onClick, children }) => (
 <button onClick={onClick} className={`px-3 py-1.5 text-xs rounded-lg border transition ${active ?"bg-primary text-white border-primary" :"text-primary border-secondary/40 hover:border-primary"}`}>{children}</button>
);
const TabButton = ({ active, onClick, children }) => (
  <button 
    onClick={onClick} 
    className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${
      active 
        ? "bg-primary text-white border-primary shadow-lg shadow-primary/25" 
        : "bg-white text-muted border-gray-200 hover:border-primary/50 hover:text-primary"
    }`}
  >
    {children}
  </button>
);
const Button = ({ children, onClick, className = "", variant = "primary", ...props }) => {
  const baseStyles = "px-6 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2";
  const variants = {
    primary: "bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/25",
    outline: "border-2 border-primary text-primary hover:bg-primary/5",
    secondary: "bg-secondary/10 text-primary hover:bg-secondary/20"
  };
  return (
    <button onClick={onClick} className={`${baseStyles} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};
const Field = ({ label, children }) => (<div><div className="text-sm text-muted">{label}</div>{children}</div>);
const Info = ({ label, children }) => (<div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-3"><div className="text-xs text-muted mb-1">{label}</div><p className="text-sm text-gray-700 whitespace-pre-line">{children}</p></div>);
const Uploader = ({ label, accept, file, setFile }) => {
 const fileInputRef = useRef(null);
 
 const handleFileSelect = (e) => {
 setFile(e.target.files?.[0] || null);
 };

 return (
 <div className="block">
 <span className="text-xs text-muted">{label}</span>
 <div className="mt-1 flex items-center gap-2">
 <input 
 ref={fileInputRef}
 type="file" 
 accept={accept} 
 onChange={handleFileSelect} 
 className="hidden"
 />
 <button
 type="button"
 onClick={() => fileInputRef.current?.click()}
 className="bg-primary text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-primary transition-colors"
 >
 Choose File
 </button>
 {file ? (
 <span className="text-xs text-muted truncate max-w-[12rem]">{file.name}</span>
 ) : (
 <span className="text-xs text-muted">No file chosen</span>
 )}
 </div>
 </div>
 );
};

function MentorModal({ onClose, messages, setMessages, onSend, technology, task }) {
  const [text, setText] = useState("");
  const [imgFile, setImgFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [quickQuestions] = useState([
    `Explain ${technology} in simple terms`,
    `What are the best practices for ${technology}?`,
    `Show me a ${technology} code example`,
    "What are common mistakes to avoid?",
    "How does this relate to real-world projects?"
  ]);
  const listRef = useRef(null);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t && !imgFile) return;
    setSending(true);
    setText("");
    const f = imgFile;
    setImgFile(null);
    await onSend({ text: t, imgFile: f });
    setSending(false);
  };

  const sendQuickQuestion = async (question) => {
    setSending(true);
    await onSend({ text: question, imgFile: null });
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className={`w-full bg-[#f0f9ff] border border-sky-200 rounded-2xl shadow-xl overflow-hidden ${isFullscreen ? 'h-full max-w-none' : 'max-w-5xl'}`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-sky-100">
          <div className="flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-black" />
            <h3 className="font-bold text-black">AI Mentor • {technology}</h3>
          </div>
          <div className="flex items-center gap-2">
            {task?.title && (
              <span className="text-xs text-black bg-bgSection px-2 py-1 rounded">
                {task.title}
              </span>
            )}
            <button
              onClick={() => setMessages([])}
              className="text-xs text-black hover:text-primary px-2 py-1 rounded hover:bg-bgSection"
              title="Clear chat history"
            >
              Clear
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="text-black hover:text-primary p-1 rounded hover:bg-bgSection"
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>
            <button className="p-1 rounded hover:bg-zinc-800" onClick={onClose}>
              <XMarkIcon className="h-5 w-5 text-black" />
            </button>
          </div>
        </div>
 
 <div className={`grid grid-rows-[1fr_auto] ${isFullscreen ? 'h-[calc(100vh-80px)]' : 'h-[75vh]'}`}>
 <div ref={listRef} className="px-5 py-4 overflow-y-auto">
 {messages.length === 0 && (
 <div className="space-y-4">
 <div className="text-center py-8">
 <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
 <ChatBubbleLeftRightIcon className="h-8 w-8 text-primary"/>
 </div>
 <h4 className="text-lg font-semibold text-primary mb-2">Your AI Learning Assistant</h4>
 <p className="text-sm text-black mb-6">
 I'm here to help you with <span className="text-primary">{technology}</span> concepts, 
 {task?.title ? <> especially with <strong className="text-black">"{task.title}"</strong></> : null}. 
 Ask me anything or use the quick questions below!
 </p>
 </div>

 {/* Quick Questions */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
 {quickQuestions.map((question, index) => (
 <button
 key={index}
 onClick={() => sendQuickQuestion(question)}
 disabled={sending}
 className="text-left p-3 rounded-lg border border-secondary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/40 transition text-sm text-black disabled:opacity-50"
 >
 {question}
 </button>
 ))}
 </div>
 </div>
 )}
 
 <ul className="space-y-4">
 {messages.map((m)=>(
 <li key={m.id} className={`flex ${m.role==="user" ?"justify-end" :"justify-start"}`}>
 <div className={`max-w-[85%] rounded-xl px-4 py-3 text-sm whitespace-pre-line ${
 m.role==="user" 
 ?"bg-primary text-white" 
 :"bg-white border border-sky-200 text-black shadow-sm"
 }`}>
 {m.typing && (
 <div className="flex items-center gap-2 text-primary">
 <div className="flex space-x-1">
 <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
 <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
 <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
 </div>
 <span className="text-xs">AI is thinking...</span>
 </div>
 )}
 {!m.typing && m.text}
 {m.imagePreview && <img src={m.imagePreview} alt="" className="mt-2 rounded-lg max-h-56" />}
 {m.when && (
 <div className="text-xs opacity-60 mt-1">
 {new Date(m.when).toLocaleTimeString()}
 </div>
 )}
 </div>
 </li>
 ))}
 </ul>
 </div>
 
 <div className="border-t border-sky-100 p-4">
 <div className="flex items-end gap-3">
 <div className="flex-1">
 <textarea 
 rows={3} 
 className="w-full rounded-lg bg-white border border-sky-200 text-sm text-black p-3 focus:outline-none focus:border-primary resize-none" 
 placeholder={`Ask me anything about ${technology}${task?.title ? ` or"${task.title}"` : ''}...`} 
 value={text} 
 onChange={(e)=>setText(e.target.value)}
 onKeyDown={(e) => {
 if (e.key === 'Enter' && !e.shiftKey) {
 e.preventDefault();
 send();
 }
 }}
 disabled={sending}
 />
 <div className="flex items-center justify-between mt-2">
 <label className="text-xs text-black flex items-center gap-2 cursor-pointer">
 <PaperClipIcon className="h-4 w-4 text-black" /> 
 {imgFile ? (
 <span className="text-primary truncate max-w-[12rem]">{imgFile.name}</span>
 ) : (
 <span className="text-black">Attach screenshot or code</span>
 )}
 <input 
 type="file" 
 accept="image/*,.txt,.js,.py,.java,.cpp,.c,.html,.css,.json" 
 className="hidden" 
 onChange={(e)=>setImgFile(e.target.files?.[0] || null)} 
 />
 </label>
 <div className="text-[11px] text-black">
 Press Enter to send, Shift+Enter for new line
 </div>
 </div>
 </div>
 <button 
 onClick={send} 
 disabled={sending || (!text.trim() && !imgFile)}
 className="rounded-xl bg-primary text-white px-6 py-3 text-sm font-semibold hover:bg-primary disabled:opacity-50 flex items-center gap-2"
 >
 {sending ? (
 <>
 <ArrowPathIcon className="h-4 w-4 animate-spin" />
 Sending...
 </>
 ) : (
 <>
 <PaperAirplaneIcon className="h-4 w-4" />
 Send
 </>
 )}
 </button>
 </div>
 </div>
 </div>
 </div>

 </div>
 );
}



